/**
 * FieldJob - Core utilities, DB layer, shared components
 * Version: 2.0
 */
import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";


// ─── OWNER / ADMIN CONFIG ─────────────────────────────────────────────────────
const OWNER_EMAIL = process.env.REACT_APP_OWNER_EMAIL || "";
const OWNER_PASSWORD = process.env.REACT_APP_OWNER_PASSWORD || "";
const isOwner = (email) => email?.toLowerCase().trim() === OWNER_EMAIL.toLowerCase();

// ─── VALIDATION UTILITIES ─────────────────────────────────────────────────────
const V = {
  // International phone validation — supports SA, US, UK, EU, AU, CA, IN, SG
  // Accepts: +[country code][number], local format with leading 0, or plain digits
  phone: (v) => {
    if (!v) return null; // optional field — only validate if filled
    const cleaned = v.replace(/[\s\-().+]/g, "");
    // Must be digits only after cleaning
    if (!/^\d+$/.test(cleaned)) return "Enter a valid phone number";
    const withPlus = v.trim().startsWith("+") ? "+" + cleaned : cleaned;
    // International format with + prefix
    if (v.trim().startsWith("+")) {
      // +27 South Africa (9 digits after code)
      if (/^\+27\d{9}$/.test("+" + cleaned)) return null;
      // +1 USA/Canada (10 digits after code)
      if (/^\+1\d{10}$/.test("+" + cleaned)) return null;
      // +44 UK (10 digits after code)
      if (/^\+44\d{10}$/.test("+" + cleaned)) return null;
      // +61 Australia (9 digits after code)
      if (/^\+61\d{9}$/.test("+" + cleaned)) return null;
      // +91 India (10 digits after code)
      if (/^\+91\d{10}$/.test("+" + cleaned)) return null;
      // +65 Singapore (8 digits after code)
      if (/^\+65\d{8}$/.test("+" + cleaned)) return null;
      // +3X/+4X/+35/+36 Europe — broad match (7-12 digits after country code)
      if (/^\+(?:3[0-9]|4[0-9]|2[0-9])\d{7,12}$/.test("+" + cleaned)) return null;
      // Generic international — 7 to 15 total digits (ITU-T E.164 standard)
      if (cleaned.length >= 7 && cleaned.length <= 15) return null;
      return "Enter a valid international phone number (e.g. +27 71 000 0000)";
    }
    // Local format — leading 0 (SA: 10 digits, UK: 11 digits, AU: 10 digits)
    if (/^0\d{9,10}$/.test(cleaned)) return null;
    // Plain digits without country code — 7 to 15 digits
    if (cleaned.length >= 7 && cleaned.length <= 15) return null;
    return "Enter a valid phone number (e.g. +27 71 000 0000 or 071 000 0000)";
  },
  // Email format
  email: (v) => {
    if (!v) return null; // optional — only validate if filled
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return "Enter a valid email address";
    return null;
  },
  // Required email
  emailRequired: (v) => {
    if (!v?.trim()) return "Email address is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return "Enter a valid email address";
    return null;
  },
  // Required text field
  required: (v, label = "This field") => {
    if (!v?.trim()) return `${label} is required`;
    return null;
  },
  // Positive number (amounts, costs, prices)
  amount: (v, label = "Amount") => {
    if (v === "" || v === null || v === undefined) return null; // optional
    const n = parseFloat(v);
    if (isNaN(n)) return `${label} must be a number`;
    if (n < 0) return `${label} cannot be negative`;
    return null;
  },
  // Required positive number
  amountRequired: (v, label = "Amount") => {
    if (v === "" || v === null || v === undefined) return `${label} is required`;
    const n = parseFloat(v);
    if (isNaN(n)) return `${label} must be a number`;
    if (n < 0) return `${label} cannot be negative`;
    if (n === 0) return `${label} must be greater than 0`;
    return null;
  },
  // Quantity — whole or decimal, non-negative
  qty: (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = parseFloat(v);
    if (isNaN(n)) return "Quantity must be a number";
    if (n < 0) return "Quantity cannot be negative";
    return null;
  },
  // Date — must not be empty
  date: (v, label = "Date") => {
    if (!v) return `${label} is required`;
    return null;
  },
  // Due date must be on or after start date
  dueDate: (due, start) => {
    if (!due || !start) return null;
    if (new Date(due) < new Date(start)) return "Due date cannot be before the start date";
    return null;
  },

  // Line items on quotes/invoices/estimates — must have description and valid price
  lineItems: (items) => {
    if (!items || items.length === 0) return "Add at least one line item";
    for (let i = 0; i < items.length; i++) {
      if (!items[i].description?.trim()) return `Line item ${i + 1} is missing a description`;
      const qty = parseFloat(items[i].qty);
      const price = parseFloat(items[i].unit_price);
      if (isNaN(qty) || qty <= 0) return `Line item ${i + 1} has an invalid quantity`;
      if (isNaN(price) || price < 0) return `Line item ${i + 1} has an invalid price`;
    }
    return null;
  },
  // Run multiple rules and return first error found
  run: (rules) => {
    for (const [field, error] of Object.entries(rules)) {
      if (error) return { field, message: error };
    }
    return null;
  },
};

// Helper — shows inline error under a form field
// Usage: <FormError error={errors.phone} />
function FormError({ error }) {
  if (!error) return null;
  return (
    <div style={{
      color: "#ef4444", fontSize: 11.5, fontWeight: 600,
      marginTop: 4, display: "flex", alignItems: "center", gap: 4,
    }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11, flexShrink: 0 }}>
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      {error}
    </div>
  );
}


// ─── NOTIFICATION SYSTEM ──────────────────────────────────────────────────────
const NotifContext = createContext(null);

function NotifProvider({ children, userId }) {
  const [toasts,  setToasts]  = useState([]);
  const [notifs,  setNotifs]  = useState([]);
  const [unread,  setUnread]  = useState(0);
  const seenIds = useRef(new Set());

  // ── Show a toast popup ────────────────────────────────────────────────────
  const showToast = (type, title, message, link) => {
    const id = Date.now().toString() + Math.random();
    setToasts(prev => [{ id, type, title, message, link }, ...prev.slice(0, 3)]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 7000);
    // Browser push if allowed
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try { new Notification(title, { body: message, icon: "/favicon.ico" }); } catch(e) {}
    }
    // PWA app badge (shows red dot on icon when installed as PWA)
    try {
      if (navigator.setAppBadge) navigator.setAppBadge();
    } catch(e) {}
  };

  const addNotif = (n) => {
    if (seenIds.current.has(n.id)) return; // dedup
    seenIds.current.add(n.id);
    setNotifs(prev => [{ ...n, read: false }, ...prev.slice(0, 49)]);
    setUnread(u => u + 1);
    showToast(n.type, n.title, n.message, n.link);
  };

  const markAllRead = () => {
    setUnread(0);
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    try { if (navigator.clearAppBadge) navigator.clearAppBadge(); } catch(e) {}
  };

  // ── On mount: load existing unread items immediately ─────────────────────
  useEffect(() => {
    if (!userId) return;

    // Request browser push permission
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    const hdrs = {
      "apikey": SUPABASE_ANON_KEY,
      "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
    };

    // Track what we've already notified about
    let lastJobReqCheck    = null;
    let lastMsgCheck       = null;
    let initialLoadDone    = false;

    const fetchAll = async () => {
      try {
        // ── 1. Pending job/booking requests ───────────────────────────────
        const reqUrl = `${SUPABASE_URL}/rest/v1/fj_job_requests?user_id=eq.${userId}&status=eq.Pending&order=created_at.desc&limit=20`;
        const reqRes = await fetch(reqUrl, { headers: hdrs });
        if (reqRes.ok) {
          const reqs = await reqRes.json();
          reqs.forEach(r => {
            const isNew = !initialLoadDone || (lastJobReqCheck && r.created_at > lastJobReqCheck);
            if (isNew || !initialLoadDone) {
              addNotif({
                id:      r.id,
                type:    "booking",
                title:   "New Booking Request",
                message: `${r.client_name || r.name || "A client"} submitted a new job request${r.description ? ": " + r.description.slice(0,60) : ""}.`,
                link:    "portals",
                time:    r.created_at,
              });
            }
          });
          if (reqs.length > 0) lastJobReqCheck = reqs[0].created_at;
        }

        // ── 2. Unread portal messages from clients ────────────────────────
        const msgUrl = `${SUPABASE_URL}/rest/v1/fj_portal_messages?user_id=eq.${userId}&sender=eq.client&read=eq.false&order=created_at.desc&limit=20`;
        const msgRes = await fetch(msgUrl, { headers: hdrs });
        if (msgRes.ok) {
          const msgs = await msgRes.json();
          msgs.forEach(m => {
            const isNew = !initialLoadDone || (lastMsgCheck && m.created_at > lastMsgCheck);
            if (isNew || !initialLoadDone) {
              addNotif({
                id:      m.id,
                type:    "message",
                title:   "New Client Message",
                message: `${m.client_name || "A client"}: ${(m.message || m.content || "").slice(0, 80)}`,
                link:    "portals",
                time:    m.created_at,
              });
            }
          });
          if (msgs.length > 0) lastMsgCheck = msgs[0].created_at;
        }

        // ── 3. Worker messages (only after initial load) ──────────────────
        if (initialLoadDone) {
          const wmUrl = `${SUPABASE_URL}/rest/v1/fj_worker_messages?order=created_at.desc&limit=10`;
          const wmRes = await fetch(wmUrl, { headers: hdrs });
          if (wmRes.ok) {
            const wmsgs = await wmRes.json();
            wmsgs.filter(m => lastMsgCheck && m.created_at > lastMsgCheck).forEach(m => {
              addNotif({
                id:      m.id,
                type:    "worker",
                title:   "Worker Message",
                message: `${m.sender_name || "A worker"}: ${(m.message || "").slice(0, 80)}`,
                link:    "jobs",
                time:    m.created_at,
              });
            });
          }
        }

        initialLoadDone = true;
      } catch(e) {
        console.warn("Notification poll error:", e.message);
      }
    };

    // Run immediately on mount
    fetchAll();

    // ── Supabase Realtime subscriptions (replaces polling) ────────────────
    // Subscribe to new job requests for this user
    const channel = new BroadcastChannel(`fj_notif_${userId}`);

    // Realtime via Supabase websocket
    const wsUrl = SUPABASE_URL.replace('https://', 'wss://') + '/realtime/v1/websocket?apikey=' + SUPABASE_ANON_KEY + '&vsn=1.0.0';
    let ws = null;
    let wsRetry = 0;

    const connectWS = () => {
      try {
        ws = new WebSocket(wsUrl);
        ws.onopen = () => {
          wsRetry = 0;
          // Subscribe to fj_job_requests inserts for this user
          ws.send(JSON.stringify({ topic: `realtime:public:fj_job_requests:user_id=eq.${userId}`, event: 'phx_join', payload: { config: { broadcast: { self: false }, presence: { key: '' } } }, ref: '1' }));
          // Subscribe to fj_portal_messages inserts for this user
          ws.send(JSON.stringify({ topic: `realtime:public:fj_portal_messages:user_id=eq.${userId}`, event: 'phx_join', payload: { config: { broadcast: { self: false }, presence: { key: '' } } }, ref: '2' }));
        };
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.event === 'INSERT' && msg.payload?.record) {
              const r = msg.payload.record;
              const topic = msg.topic || '';
              if (topic.includes('fj_job_requests')) {
                addNotif({ id: r.id, type: 'booking', title: 'New Booking Request', message: `${r.client_name || 'A client'} submitted a new job request.`, link: 'portals', time: r.created_at });
              } else if (topic.includes('fj_portal_messages') && r.sender === 'client') {
                addNotif({ id: r.id, type: 'message', title: 'New Client Message', message: `${r.client_name || 'A client'}: ${(r.message || '').slice(0, 80)}`, link: 'portals', time: r.created_at });
              }
            }
          } catch {}
        };
        ws.onclose = () => {
          // Exponential backoff reconnect — max 60s
          const delay = Math.min(1000 * Math.pow(2, wsRetry++), 60000);
          setTimeout(connectWS, delay);
        };
        ws.onerror = () => ws.close();
      } catch {}
    };
    connectWS();

    // Fallback poll every 60s (reduced from 20s) for missed realtime events
    const interval = setInterval(fetchAll, 60000);
    return () => {
      clearInterval(interval);
      if (ws) ws.close();
    };
  }, [userId]);

  return (
    <NotifContext.Provider value={{ notifs, unread, markAllRead }}>
      {children}

      {/* ── TOAST STACK ────────────────────────────────────────────────── */}
      <div style={{
        position: "fixed", top: 20, right: 20, zIndex: 99999,
        display: "flex", flexDirection: "column", gap: 10,
        maxWidth: 360, pointerEvents: "none",
      }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            background: "white",
            borderRadius: 14,
            padding: "14px 16px",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
            borderLeft: `4px solid ${t.type === "booking" ? "#c8972a" : t.type === "message" ? "#3b82f6" : "#10b981"}`,
            animation: "slideInRight 0.35s cubic-bezier(0.34,1.56,0.64,1)",
            pointerEvents: "all",
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: t.type === "booking" ? "rgba(200,151,42,0.12)" : t.type === "message" ? "#eff6ff" : "#f0fdf4",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
            }}>
              {t.type === "booking" ? (
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:20,height:20,stroke:"#c8972a"}}><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="12" y2="16"/></svg>
            ) : t.type === "message" ? (
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:20,height:20,stroke:"#3b82f6"}}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:20,height:20,stroke:"#10b981"}}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#0f1923", marginBottom: 3 }}>{t.title}</div>
              <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{t.message}</div>
            </div>
            <button
              onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
              style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "0 0 0 4px", flexShrink: 0 }}>
              ×
            </button>
          </div>
        ))}
      </div>
    </NotifContext.Provider>
  );
}

const useNotif = () => useContext(NotifContext);

// ─── SUPABASE CONFIG ──────────────────────────────────────────────────────────
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

// ─── SUPABASE CLIENT ──────────────────────────────────────────────────────────
// Uses real Supabase Auth JWT tokens for all data requests.
// After login, sb_token (JWT) is used instead of anon key so RLS
// policies can identify the user via auth.uid().
const getAuthHeader = () => {
  const token = localStorage.getItem("sb_token");
  return token ? `Bearer ${token}` : `Bearer ${SUPABASE_ANON_KEY}`;
};

const supabase = {
  url: SUPABASE_URL,
  key: SUPABASE_ANON_KEY,
  headers: () => ({
    "Content-Type": "application/json",
    "apikey": SUPABASE_ANON_KEY,
    "Authorization": getAuthHeader(),
  }),
  async from(table) {
    const base = `${SUPABASE_URL}/rest/v1/${table}`;
    const h = () => ({ "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY, "Authorization": getAuthHeader() });
    return {
      select: async (cols = "*", opts = {}) => {
        let url = `${base}?select=${cols}`;
        if (opts.eq) Object.entries(opts.eq).forEach(([k, v]) => url += `&${k}=eq.${v}`);
        const r = await fetch(url, { headers: h() });
        return { data: await r.json(), error: r.ok ? null : "Error" };
      },
      insert: async (data) => {
        const r = await fetch(base, { method: "POST", headers: { ...h(), "Prefer": "return=representation" }, body: JSON.stringify(data) });
        return { data: await r.json(), error: r.ok ? null : "Error" };
      },
      update: async (data, match) => {
        let url = base + "?";
        Object.entries(match).forEach(([k, v]) => url += `${k}=eq.${v}&`);
        const r = await fetch(url, { method: "PATCH", headers: { ...h(), "Prefer": "return=representation" }, body: JSON.stringify(data) });
        return { data: await r.json(), error: r.ok ? null : "Error" };
      },
      delete: async (match) => {
        let url = base + "?";
        Object.entries(match).forEach(([k, v]) => url += `${k}=eq.${v}&`);
        const r = await fetch(url, { method: "DELETE", headers: h() });
        return { error: r.ok ? null : "Error" };
      },
    };
  },
  auth: {
    // Sign in — returns real JWT token, stores in localStorage
    signInWithPassword: async ({ email, password }) => {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (d.access_token) {
        localStorage.setItem("sb_token", d.access_token);
        if (d.refresh_token) localStorage.setItem("sb_refresh_token", d.refresh_token);
      }
      return { data: d, error: d.error_description || d.error ? { message: d.error_description || d.error } : null };
    },
    // Refresh expired JWT using refresh token
    refreshSession: async () => {
      const refreshToken = localStorage.getItem("sb_refresh_token");
      if (!refreshToken) return { data: null, error: { message: "No refresh token" } };
      const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      const d = await r.json();
      if (d.access_token) {
        localStorage.setItem("sb_token", d.access_token);
        if (d.refresh_token) localStorage.setItem("sb_refresh_token", d.refresh_token);
        return { data: d, error: null };
      }
      return { data: null, error: { message: d.error_description || "Session expired" } };
    },
    // Verify JWT is still valid
    getUser: async () => {
      const token = localStorage.getItem("sb_token");
      if (!token) return { data: null, error: { message: "No token" } };
      const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY, "Authorization": `Bearer ${token}` },
      });
      if (!r.ok) return { data: null, error: { message: "Token invalid" } };
      return { data: { user: await r.json() }, error: null };
    },
    // Send password reset email — goes through Resend SMTP automatically
    resetPasswordForEmail: async (email) => {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY },
        body: JSON.stringify({ email }),
      });
      return { error: r.ok ? null : { message: "Failed to send reset email" } };
    },
    // Sign out — invalidate token server-side and clear locally
    signOut: async () => {
      const token = localStorage.getItem("sb_token");
      if (token) {
        try {
          await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY, "Authorization": `Bearer ${token}` },
          });
        } catch {}
      }
      localStorage.removeItem("sb_token");
      localStorage.removeItem("sb_refresh_token");
      localStorage.removeItem("fj_user");
      return { error: null };
    },
    getSession: () => {
      const token = localStorage.getItem("sb_token");
      const user = localStorage.getItem("fj_user");
      return { data: { session: token ? { access_token: token, user: user ? JSON.parse(user) : null } : null } };
    },
    // Create user in Supabase Auth — used in owner approve flow
    // Requires service role key — routed through Edge Function
    adminCreateUser: async (email, password) => {
      const r = await fetch(`${SUPABASE_URL}/functions/v1/admin-create-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      return { data: d, error: d.error ? { message: d.error } : null };
    },
  },
};

// ─── THEME & STYLES ───────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&family=Playfair+Display:wght@700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink: #0f1923;
    --ink-light: #2d3f50;
    --surface: #f4f5f7;
    --card: #ffffff;
    --border: #dde1e7;
    --border-light: #eaedf1;
    --gold: #c8972a;
    --gold-dim: rgba(200,151,42,0.12);
    --gold-light: #f0d078;
    --emerald: #10b981;
    --ruby: #ef4444;
    --amber: #f59e0b;
    --sky: #0ea5e9;
    --purple: #8b5cf6;
    --radius: 8px;
    --shadow: 0 1px 3px rgba(0,0,0,0.07), 0 2px 8px rgba(0,0,0,0.04);
    --shadow-lg: 0 8px 32px rgba(0,0,0,0.12);
    --font: 'IBM Plex Sans', sans-serif;
    --mono: 'IBM Plex Mono', monospace;
    --font-display: 'Playfair Display', serif;

    /* Professional status colors */
    --status-green-bg: #edf7f1; --status-green-fg: #1a7a45;
    --status-amber-bg: #fdf4e3; --status-amber-fg: #946010;
    --status-blue-bg:  #eaf2fc; --status-blue-fg:  #1a5fa8;
    --status-grey-bg:  #f1f2f4; --status-grey-fg:  #5a6270;
    --status-red-bg:   #fdeeed; --status-red-fg:   #b72b2b;
    --status-navy-bg:  #eef1f8; --status-navy-fg:  #2d4a8a;
  }

  body { font-family: var(--font); background: var(--surface); color: var(--ink); font-size: 14px; line-height: 1.6; -webkit-font-smoothing: antialiased; }

  html { overflow-x: hidden; }
  body { overflow-x: hidden; max-width: 100%; }
  .app { display: flex; min-height: 100vh; }

  /* ── SIDEBAR (mini icon rail, expands on hover) ── */
  .sidebar {
    width: 62px; background: #0c1117; color: white; display: flex; flex-direction: column;
    position: fixed; top: 0; left: 0; height: 100vh; z-index: 100;
    transition: width 0.22s cubic-bezier(0.4,0,0.2,1);
    border-right: 1px solid rgba(255,255,255,0.06);
    overflow: hidden;
  }
  .sidebar:hover { width: 228px; }
  .sidebar-logo {
    padding: 16px 14px 14px; border-bottom: 1px solid rgba(255,255,255,0.06);
    display: flex; align-items: center; gap: 10px; min-height: 64px; flex-shrink: 0;
  }
  .sidebar-logo-mark {
    width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid rgba(200,170,75,0.6);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    background: rgba(200,170,75,0.06);
  }
  .sidebar-logo-mark span { font-size: 12px; font-weight: 700; color: #fff; letter-spacing: -0.5px; }
  .sidebar-logo-text { opacity: 0; transition: opacity 0.15s 0.05s; white-space: nowrap; overflow: hidden; }
  .sidebar:hover .sidebar-logo-text { opacity: 1; }
  .sidebar-logo-text h1 { font-family: var(--font-display); font-size: 16px; color: rgba(255,255,255,0.92); letter-spacing: 0.3px; line-height: 1.2; }
  .sidebar-logo-text span { font-size: 8.5px; color: rgba(255,255,255,0.28); letter-spacing: 2px; text-transform: uppercase; }
  .sidebar-nav { flex: 1; padding: 10px 0; overflow-y: auto; overflow-x: hidden; }
  .sidebar-nav::-webkit-scrollbar { width: 3px; }
  .sidebar-nav::-webkit-scrollbar-thumb { background: rgba(200,151,42,0.3); border-radius: 3px; }
  .nav-section {
    padding: 10px 14px 3px; font-size: 8.5px; letter-spacing: 1.8px; text-transform: uppercase;
    color: rgba(255,255,255,0.18); white-space: nowrap; overflow: hidden;
    opacity: 0; height: 0; transition: opacity 0.12s, height 0.12s;
  }
  .sidebar:hover .nav-section { opacity: 1; height: auto; }
  .nav-item {
    display: flex; align-items: center; gap: 0; padding: 5px 9px; margin: 1px 5px;
    color: rgba(255,255,255,0.5); cursor: pointer; transition: all 0.15s; font-size: 13px;
    border-radius: 9px; white-space: nowrap; overflow: hidden;
  }
  .nav-item:hover { color: white; background: rgba(255,255,255,0.06); }
  .nav-item.active { color: #fff; background: rgba(255,255,255,0.1); }
  .nav-item svg { width: 14px; height: 14px; flex-shrink: 0; }
  /* ── Icon tile ── */
  .nav-icon-tile {
    width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);
    transition: all 0.15s; margin-right: 10px;
  }
  .nav-item.active .nav-icon-tile {
    background: rgba(200,151,42,0.18);
    border-color: rgba(200,151,42,0.4);
  }
  .nav-item:hover .nav-icon-tile { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.12); }
  .nav-item-label { opacity: 0; transition: opacity 0.1s 0.06s; font-size: 12.5px; font-weight: 500; }
  .sidebar:hover .nav-item-label { opacity: 1; }
  /* ── Bottom nav (mobile only — hidden by default) ── */
  .bottom-nav { display: none; position: fixed; bottom: 0; left: 0; right: 0; z-index: 9999; background: var(--ink); border-top: 1px solid rgba(255,255,255,0.08); padding: 8px 0 max(12px, env(safe-area-inset-bottom)); }
  .bottom-nav-inner { display: flex; align-items: flex-start; width: 100%; }
  .bn-item { flex:1; display:flex; flex-direction:column; align-items:center; gap:4px; padding:4px 0; cursor:pointer; color:rgba(255,255,255,0.35); font-size:9.5px; font-weight:600; transition:color 0.15s; }
  .bn-item.active { color: var(--gold-light); }
  .bn-tile { width:36px; height:36px; border-radius:11px; display:flex; align-items:center; justify-content:center; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07); transition:all 0.15s; position:relative; overflow:hidden; }
  .bn-tile::after { content:''; position:absolute; top:0; left:0; right:0; height:50%; background:linear-gradient(rgba(255,255,255,0.04),transparent); }
  .bn-item.active .bn-tile { background:linear-gradient(145deg,rgba(200,151,42,0.3),rgba(200,151,42,0.12)); border-color:rgba(200,151,42,0.5); box-shadow:0 0 12px rgba(200,151,42,0.2),inset 0 1px 0 rgba(255,255,255,0.1); }
  .bn-tile svg { width:17px; height:17px; position:relative; z-index:1; }
  .bn-item.active .bn-tile svg { color: var(--gold-light); }
  /* ── Quick action tiles ── */
  .qa-tile { width:56px; height:56px; border-radius:17px; display:flex; align-items:center; justify-content:center; background:linear-gradient(145deg,rgba(200,151,42,0.22),rgba(200,151,42,0.07)); border:1px solid rgba(200,151,42,0.38); box-shadow:0 2px 14px rgba(200,151,42,0.12),inset 0 1px 0 rgba(255,255,255,0.08); position:relative; overflow:hidden; cursor:pointer; transition:all 0.15s; }
  .qa-tile::before { content:''; position:absolute; top:-8px; right:-8px; width:28px; height:28px; background:rgba(200,151,42,0.15); border-radius:50%; filter:blur(8px); }
  .qa-tile::after { content:''; position:absolute; top:0; left:0; right:0; height:50%; background:linear-gradient(rgba(255,255,255,0.07),transparent); border-radius:17px 17px 0 0; }
  .qa-tile:active { transform: scale(0.95); }
  .qa-tile svg { width:24px; height:24px; color:var(--gold-light); position:relative; z-index:1; }
  /* ── Mobile header (hidden by default) ── */
  .mobile-header { display: none; background: var(--ink); padding: 14px 18px 0; }
  .mobile-header-top { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:4px; }
  .mobile-greeting { font-size:11px; color:rgba(255,255,255,0.38); margin-bottom:2px; }
  .mobile-biz-name { font-family:var(--font-display); font-size:19px; color:white; font-weight:700; }
  .mobile-date { font-size:10.5px; color:rgba(255,255,255,0.3); margin-top:1px; }
  .mobile-avatar { width:36px; height:36px; border-radius:50%; background:linear-gradient(135deg,var(--gold),#b8841e); display:flex; align-items:center; justify-content:center; font-weight:700; font-size:14px; color:var(--ink); flex-shrink:0; }
  /* ── KPI strip ── */
  .kpi-strip { display:flex; gap:10px; padding:14px 18px 16px; overflow-x:auto; background:var(--ink); }
  .kpi-strip::-webkit-scrollbar { display:none; }
  .kpi-chip { background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.1); border-radius:12px; padding:10px 14px; min-width:128px; flex-shrink:0; cursor:pointer; }
  .kpi-chip.gold { background:rgba(200,151,42,0.14); border-color:rgba(200,151,42,0.35); }
  .kpi-chip-label { font-size:9.5px; color:rgba(255,255,255,0.38); text-transform:uppercase; letter-spacing:0.6px; margin-bottom:4px; font-weight:600; }
  .kpi-chip-val { font-size:17px; font-weight:700; font-family:var(--mono); color:white; }
  .kpi-chip.gold .kpi-chip-val { color:var(--gold-light); }
  .kpi-chip-sub { font-size:10px; color:rgba(255,255,255,0.3); margin-top:2px; }
  /* ── Quick actions row (hidden by default) ── */
  .quick-actions-row { display:none; background:white; padding:14px 18px 10px; border-bottom:1px solid var(--border); }
  .qa-row-inner { display:flex; gap:14px; overflow-x:auto; }
  .qa-row-inner::-webkit-scrollbar { display:none; }
  .qa-btn { display:flex; flex-direction:column; align-items:center; gap:7px; cursor:pointer; }
  .qa-btn-label { font-size:10.5px; font-weight:600; color:var(--ink); text-align:center; white-space:nowrap; }
  /* ── Calendar view toggle ── */
  .cal-desktop-view { display: block; }
  .cal-mobile-view { display: none; }
  .cal-view-toggle { display:none; background:var(--surface); border-radius:8px; padding:3px; border:1px solid var(--border); }
  .cal-vt-btn { padding:5px 12px; border-radius:6px; border:none; background:none; font-size:12px; font-weight:600; color:#64748b; cursor:pointer; transition:all 0.15s; }
  .cal-vt-btn.active { background:white; color:var(--ink); box-shadow:0 1px 4px rgba(0,0,0,0.1); }
  .sidebar-footer { padding: 12px 9px 14px; border-top: 1px solid rgba(255,255,255,0.07); flex-shrink: 0; overflow: hidden; }
  .sidebar-footer .business-name { font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.85); white-space: nowrap; opacity: 0; transition: opacity 0.1s 0.05s; }
  .sidebar:hover .sidebar-footer .business-name { opacity: 1; }
  .sidebar-footer .plan-badge {
    display: inline-flex; align-items: center; gap: 4px;
    background: rgba(200,151,42,0.18); color: var(--gold-light);
    padding: 2px 8px; border-radius: 4px; font-size: 10.5px; margin-top: 4px;
    opacity: 0; transition: opacity 0.1s 0.05s;
  }
  .sidebar:hover .sidebar-footer .plan-badge { opacity: 1; }
  .sidebar:hover .sidebar-signout { opacity: 1 !important; }
  .sidebar-signout:hover { color: rgba(255,255,255,0.7) !important; }

  /* ── MAIN ── */
  .main { margin-left: 62px; flex: 1; display: flex; flex-direction: column; min-height: 100vh; min-width: 0; }
  .topbar {
    background: var(--card); border-bottom: 1px solid var(--border); padding: 0 24px;
    height: 54px; display: flex; align-items: center; justify-content: space-between;
    position: sticky; top: 0; z-index: 50;
  }
  .topbar h2 { font-size: 15px; font-weight: 700; color: var(--ink); }
  .topbar-actions { display: flex; align-items: center; gap: 10px; }
  .page { padding: 22px 24px; flex: 1; }

  /* ── KPI CARDS ── */
  .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 18px; }
  .stat-card {
    background: var(--card); border-radius: var(--radius); padding: 16px 18px;
    border: 1px solid var(--border); box-shadow: var(--shadow);
    position: relative; overflow: hidden;
  }
  .stat-card::before {
    content: ''; position: absolute; left: 0; top: 0; bottom: 0;
    width: 3px; background: var(--border); border-radius: 8px 0 0 8px;
  }
  .stat-card.kpi-gold::before  { background: var(--gold); }
  .stat-card.kpi-warn::before  { background: #b45309; }
  .stat-card.kpi-blue::before  { background: var(--status-blue-fg); }
  .stat-card.kpi-green::before { background: var(--status-green-fg); }
  .stat-card.kpi-red::before   { background: var(--status-red-fg); }

  .stat-top { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 10px; }
  .stat-label { font-size: 10.5px; font-weight: 600; color: var(--status-grey-fg); text-transform: uppercase; letter-spacing: 0.6px; }
  .stat-icon { width: 30px; height: 30px; border-radius: 6px; background: var(--surface); border: 1px solid var(--border-light); display: flex; align-items: center; justify-content: center; }
  .stat-value { font-family: var(--mono); font-size: 20px; font-weight: 600; color: var(--ink); letter-spacing: -0.5px; margin-bottom: 5px; }
  .stat-card.kpi-gold .stat-value { color: #a07020; }
  .stat-card.kpi-red .stat-value  { color: var(--status-red-fg); }
  .stat-sub { font-size: 11px; color: var(--status-grey-fg); padding-top: 7px; border-top: 1px solid var(--border-light); display: flex; align-items: center; gap: 5px; }

  .stat-badge { display: inline-flex; align-items: center; font-size: 10px; font-weight: 600; padding: 1px 6px; border-radius: 3px; }
  .sb-green { background: var(--status-green-bg); color: var(--status-green-fg); }
  .sb-amber { background: var(--status-amber-bg); color: var(--status-amber-fg); }
  .sb-red   { background: var(--status-red-bg);   color: var(--status-red-fg); }
  .sb-grey  { background: var(--status-grey-bg);  color: var(--status-grey-fg); }
  .sb-blue  { background: var(--status-blue-bg);  color: var(--status-blue-fg); }

  /* ── PANELS ── */
  .card { background: var(--card); border-radius: var(--radius); box-shadow: var(--shadow); border: 1px solid var(--border); overflow: hidden; }
  .card-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; border-bottom: 1px solid var(--border-light); background: #fafbfc; }
  .card-body { padding: 18px 20px; }
  .card-title { font-size: 12.5px; font-weight: 700; color: var(--ink); }
  .card-action { font-size: 11px; color: var(--gold); font-weight: 600; cursor: pointer; }

  /* ── BUTTONS ── */
  .btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 6px; font-size: 12.5px; font-weight: 600; cursor: pointer; border: none; transition: all 0.15s; font-family: var(--font); }
  .btn-primary { background: var(--ink); color: white; }
  .btn-primary:hover { background: var(--ink-light); }
  .btn-gold { background: var(--gold); color: white; }
  .btn-gold:hover { background: #b8872a; }
  .btn-success { background: var(--status-green-fg); color: white; }
  .btn-danger { background: var(--status-red-fg); color: white; }
  .btn-outline { background: transparent; border: 1px solid var(--border); color: var(--ink-light); }
  .btn-outline:hover { border-color: var(--ink); background: var(--surface); }
  .btn-sm { padding: 5px 10px; font-size: 11.5px; }
  .btn-lg { padding: 11px 22px; font-size: 14px; }
  .btn-icon { padding: 6px; border-radius: 6px; }

  /* ── FORMS ── */
  .form-group { margin-bottom: 15px; }
  .form-group label { display: block; font-size: 12px; font-weight: 600; margin-bottom: 5px; color: var(--ink-light); }
  .mobile-scroll-hint { display: none; }
  .form-group textarea.form-control { width: 100%; display: block; }
  .form-label { display: block; font-size: 12.5px; font-weight: 600; margin-bottom: 5px; color: var(--ink-light); }
  .form-input, .form-select, .form-textarea {
    width: 100%; padding: 8px 11px; border: 1px solid var(--border); border-radius: 6px;
    font-family: var(--font); font-size: 13px; color: var(--ink); background: white;
    transition: border-color 0.15s; outline: none;
  }
  .form-input:focus, .form-select:focus, .form-textarea:focus { border-color: var(--gold); box-shadow: 0 0 0 3px rgba(200,151,42,0.1); }
  .form-textarea { resize: vertical; min-height: 80px; }
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .form-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }

  /* ── TABLE ── */
  .table-wrap { overflow-x: auto; }
  .compliance-mobile-cards { display: none; }
  .expense-mobile-cards { display: none; }
  .expense-mobile-summary { display: none; }
  .expense-desktop-sidebar { display: block; }
  .inventory-desktop-table { display: block; }
  .inventory-mobile-cards { display: none; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; padding: 10px 18px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.7px; color: var(--status-grey-fg); border-bottom: 1px solid var(--border); font-weight: 700; background: #fafbfc; }
  td { padding: 11px 18px; border-bottom: 1px solid var(--border-light); font-size: 13px; color: var(--status-grey-fg); vertical-align: middle; }
  tr:last-child td { border-bottom: none; }
  tr:hover td { background: #fafbfc; }
  .td-primary { font-weight: 600; color: var(--ink); font-size: 13px; }
  .td-sub { font-size: 11px; color: var(--status-grey-fg); margin-top: 2px; }
  .td-mono { font-family: var(--mono); font-size: 12.5px; font-weight: 500; color: var(--ink); }

  /* ── BADGES ── */
  .badge { display: inline-flex; align-items: center; padding: 2px 7px; border-radius: 3px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.2px; }
  .badge-green  { background: var(--status-green-bg); color: var(--status-green-fg); }
  .badge-amber  { background: var(--status-amber-bg); color: var(--status-amber-fg); }
  .badge-red    { background: var(--status-red-bg);   color: var(--status-red-fg); }
  .badge-blue   { background: var(--status-blue-bg);  color: var(--status-blue-fg); }
  .badge-purple { background: #ede9fe; color: #6d28d9; }
  .badge-gray   { background: var(--status-grey-bg);  color: var(--status-grey-fg); }
  .badge-gold   { background: var(--gold-dim); color: var(--gold); }

  /* ── MODAL ── */
  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 16px; animation: fadeIn 0.15s ease; }
  .modal { background: white; border-radius: 12px; width: 100%; max-width: 640px; max-height: 90vh; overflow-y: auto; box-shadow: var(--shadow-lg); animation: slideUp 0.2s ease; }
  .modal-lg { max-width: 800px; }
  .modal-header { padding: 18px 22px 0; display: flex; align-items: center; justify-content: space-between; }
  .modal-header h3 { font-size: 15px; font-weight: 700; }
  .modal-body { padding: 18px 22px; }
  .modal-footer { padding: 0 22px 18px; display: flex; gap: 8px; justify-content: flex-end; }

  @keyframes fadeIn  { from { opacity: 0; }              to { opacity: 1; } }
  @keyframes slideUp { from { transform: translateY(14px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

  /* ── STATUS PIPELINE ── */
  .pipeline { display: flex; gap: 6px; flex-wrap: wrap; }
  .pipeline-step { padding: 3px 10px; border-radius: 4px; font-size: 11.5px; font-weight: 600; cursor: pointer; border: 1px solid; transition: all 0.15s; }

  /* ── HEALTH ── */
  .health-indicator { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: 6px; }
  .health-dot { width: 8px; height: 8px; border-radius: 50%; animation: pulse 2s infinite; }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

  /* ── SEGMENT TABS ── */
  .tabs { display: flex; gap: 0; border-bottom: 1px solid var(--border); margin-bottom: 16px; }
  .tab { padding: 9px 18px; font-size: 13px; cursor: pointer; color: var(--status-grey-fg); border-bottom: 2px solid transparent; margin-bottom: -1px; transition: all 0.15s; font-weight: 500; }
  .tab.active { color: var(--gold); border-bottom-color: var(--gold); font-weight: 600; }
  .tab:hover { color: var(--ink); }

  /* ── SEARCH ── */
  .search-bar { display: flex; align-items: center; gap: 8px; background: white; border: 1px solid var(--border); border-radius: 6px; padding: 7px 11px; }
  .search-bar input { border: none; outline: none; font-size: 13px; font-family: var(--font); flex: 1; }

  /* ── PROGRESS ── */
  .progress-bar { background: var(--border); border-radius: 3px; height: 4px; overflow: hidden; }
  .progress-fill { height: 100%; border-radius: 3px; transition: width 0.4s ease; background: var(--gold); }

  /* ── ALERTS ── */
  .alert { padding: 10px 14px; border-radius: 6px; font-size: 12.5px; margin-bottom: 14px; border: 1px solid; display: flex; align-items: center; gap: 8px; }
  .alert-error   { background: var(--status-red-bg);   color: var(--status-red-fg);   border-color: #f5c2c2; }
  .alert-success { background: var(--status-green-bg); color: var(--status-green-fg); border-color: #c3e8d0; }
  .alert-info    { background: var(--status-blue-bg);  color: var(--status-blue-fg);  border-color: #b3d4f5; }
  .alert-warning { background: var(--status-amber-bg); color: var(--status-amber-fg); border-color: #f5dba0; }

  /* ── EMPTY STATE ── */
  .empty-state { text-align: center; padding: 56px 20px; color: #94a3b8; }
  .empty-state svg { margin-bottom: 14px; opacity: 0.35; }
  .empty-state h3 { font-size: 15px; color: var(--status-grey-fg); margin-bottom: 6px; }

  /* ── LANDING ── */
  .landing { min-height: 100vh; background: var(--ink); }
  .landing-nav { display: flex; align-items: center; justify-content: space-between; padding: 20px 60px; position: sticky; top: 0; background: rgba(15,25,35,0.95); backdrop-filter: blur(10px); z-index: 100; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .landing-logo { font-family: var(--font-display); font-size: 26px; color: var(--gold-light); }
  .landing-nav-links { display: flex; gap: 32px; }
  .landing-nav-links a { color: rgba(255,255,255,0.7); text-decoration: none; font-size: 14px; transition: color 0.15s; }
  .landing-nav-links a:hover { color: white; }
  .hero { padding: 100px 60px 80px; text-align: center; background: radial-gradient(ellipse 80% 60% at 50% -10%, rgba(200,151,42,0.15) 0%, transparent 60%); }
  .hero-eyebrow { font-size: 11px; letter-spacing: 3px; text-transform: uppercase; color: var(--gold); margin-bottom: 20px; }
  .hero h1 { font-family: var(--font-display); font-size: 54px; color: white; line-height: 1.15; margin-bottom: 24px; max-width: 700px; margin-left: auto; margin-right: auto; }
  .hero-sub { font-size: 18px; color: rgba(255,255,255,0.6); max-width: 520px; margin: 0 auto 40px; line-height: 1.7; }
  .hero-buttons { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
  .btn-hero-primary { background: var(--gold); color: white; padding: 13px 28px; font-size: 14px; border-radius: 8px; border: none; cursor: pointer; font-weight: 600; transition: all 0.15s; font-family: var(--font); }
  .btn-hero-primary:hover { background: #b8872a; transform: translateY(-1px); }
  .btn-hero-outline { background: transparent; color: white; padding: 13px 28px; font-size: 14px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.3); cursor: pointer; transition: all 0.15s; font-family: var(--font); }
  .btn-hero-outline:hover { border-color: white; background: rgba(255,255,255,0.05); }
  .features-section { padding: 80px 60px; }
  .section-title { font-family: var(--font-display); font-size: 36px; color: white; text-align: center; margin-bottom: 12px; }
  .section-sub { text-align: center; color: rgba(255,255,255,0.5); margin-bottom: 52px; font-size: 16px; }
  .features-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px; max-width: 1000px; margin: 0 auto; }
  .feature-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 26px; transition: all 0.2s; }
  .feature-card:hover { background: rgba(255,255,255,0.07); transform: translateY(-2px); }
  .feature-icon { width: 40px; height: 40px; background: rgba(200,151,42,0.15); border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; font-size: 18px; }
  .feature-card h3 { font-size: 15px; font-weight: 600; color: white; margin-bottom: 8px; }
  .feature-card p { font-size: 13px; color: rgba(255,255,255,0.5); line-height: 1.7; }
  .pricing-section { padding: 80px 60px; background: rgba(255,255,255,0.02); }
  .pricing-card { max-width: 420px; margin: 0 auto; background: rgba(255,255,255,0.05); border: 1px solid rgba(200,151,42,0.3); border-radius: 16px; padding: 40px; text-align: center; }
  .pricing-amount { font-size: 56px; font-weight: 700; color: var(--gold-light); line-height: 1; }
  .pricing-period { color: rgba(255,255,255,0.5); font-size: 15px; }
  .pricing-features { list-style: none; text-align: left; margin: 28px 0; }
  .pricing-features li { padding: 8px 0; border-bottom: 1px solid rgba(255,255,255,0.06); font-size: 14px; color: rgba(255,255,255,0.75); display: flex; align-items: center; gap: 10px; }
  .pricing-features li::before { content: "✓"; color: var(--gold); font-weight: 700; }

  /* ── AUTH ── */
  .auth-page { min-height: 100vh; background: var(--ink); display: flex; align-items: center; justify-content: center; padding: 20px; }
  .auth-card { background: white; border-radius: 14px; padding: 36px; width: 100%; max-width: 420px; }
  .auth-logo { font-family: var(--font-display); font-size: 26px; color: var(--gold); text-align: center; margin-bottom: 6px; }
  .auth-subtitle { text-align: center; color: #64748b; font-size: 13.5px; margin-bottom: 26px; }

  /* ── CALENDAR ── */
  .calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 1px; background: var(--border); border-radius: 8px; overflow: hidden; }
  .cal-header { background: var(--ink); color: rgba(255,255,255,0.7); text-align: center; padding: 8px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
  .cal-cell { background: white; min-height: 88px; padding: 6px; cursor: pointer; transition: background 0.1s; }
  .cal-cell:hover { background: #f8fafc; }
  .cal-cell.other-month { background: #f8fafc; }
  .cal-cell.today { background: rgba(200,151,42,0.04); }
  .cal-date { font-size: 11.5px; font-weight: 600; margin-bottom: 3px; width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; border-radius: 50%; }
  .cal-date.today-dot { background: var(--gold); color: white; }
  .cal-job { background: var(--status-blue-bg); color: var(--status-blue-fg); border-radius: 3px; padding: 2px 5px; font-size: 10px; font-weight: 600; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer; }
  .cal-job.completed  { background: var(--status-green-bg); color: var(--status-green-fg); }
  .cal-job.pending    { background: var(--status-amber-bg); color: var(--status-amber-fg); }
  .cal-job.urgent     { background: var(--status-red-bg);   color: var(--status-red-fg); }
  .cal-job.inprogress { background: var(--status-blue-bg);  color: var(--status-blue-fg); }
  .cal-cell-active { background: #fffdf5 !important; }
  .cal-cell-active:hover { background: #fff8e6 !important; }
  .cal-desktop-view { display: block; }
  .cal-mobile-view { display: none; }
  .cal-view-toggle { display: none; background: var(--surface); border-radius: 8px; padding: 3px; border: 1px solid var(--border); }
  .cal-vt-btn { padding: 5px 12px; border-radius: 6px; border: none; background: none; font-size: 12px; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.15s; }
  .cal-vt-btn.active { background: white; color: var(--ink); box-shadow: 0 1px 4px rgba(0,0,0,0.1); }

  /* ── DOC PREVIEW ── */
  .doc-preview { background: white; padding: 40px; max-width: 680px; margin: 0 auto; font-size: 13px; }
  .doc-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; }
  .doc-company h2 { font-size: 22px; font-weight: 700; color: var(--ink); }
  .doc-number { font-size: 26px; font-weight: 700; color: var(--gold); text-align: right; font-family: var(--mono); }
  .doc-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 28px; padding: 18px; background: var(--surface); border-radius: 6px; }
  .doc-table th { background: var(--ink); color: white; }
  .doc-total-row { font-weight: 700; background: rgba(200,151,42,0.05); }
  .doc-banking { margin-top: 24px; padding: 14px; background: var(--surface); border-radius: 6px; }

  /* ── CASHFLOW CHART ── */
  .chart-bar { display: flex; align-items: flex-end; gap: 4px; height: 120px; }
  .bar-item { flex: 1; border-radius: 3px 3px 0 0; transition: height 0.3s ease; cursor: pointer; }
  .bar-label { font-size: 10px; color: var(--status-grey-fg); text-align: center; margin-top: 4px; }

  /* ── WHATSAPP / EMAIL ── */
  .whatsapp-btn { background: #25d366; color: white; display: inline-flex; align-items: center; gap: 8px; padding: 9px 16px; border-radius: 6px; border: none; font-size: 13px; cursor: pointer; font-family: var(--font); font-weight: 600; }
  .whatsapp-btn:hover { background: #1fb956; }
  .email-btn { background: var(--status-blue-fg); color: white; display: inline-flex; align-items: center; gap: 8px; padding: 9px 16px; border-radius: 6px; border: none; font-size: 13px; cursor: pointer; font-family: var(--font); font-weight: 600; }
  .email-btn:hover { background: #1450a0; }

  /* ── DEMO BANNER ── */
  .demo-banner { background: linear-gradient(135deg, #1a2840, #0f1923); border-bottom: 2px solid var(--gold); padding: 9px 24px; display: flex; align-items: center; justify-content: space-between; }
  .demo-banner span { color: var(--gold-light); font-size: 12.5px; }
  .demo-banner strong { color: white; }

  /* ── LINE ITEMS ── */
  .line-item-row { display: grid; grid-template-columns: 3fr 1fr 1.5fr 1.5fr 40px; gap: 8px; align-items: center; margin-bottom: 8px; }

  /* ── CATEGORY PILL ── */
  .cat-pill { display: inline-flex; align-items: center; gap: 5px; padding: 2px 7px; border-radius: 3px; font-size: 11px; font-weight: 600; background: var(--status-grey-bg); color: var(--status-grey-fg); }
  .cat-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

  /* ── MISC ── */
  .divider { border: none; border-top: 1px solid var(--border); margin: 18px 0; }
  .text-muted { color: var(--status-grey-fg); }
  .text-sm { font-size: 12px; }
  .mt-1 { margin-top: 4px; } .mt-2 { margin-top: 8px; } .mt-3 { margin-top: 12px; } .mt-4 { margin-top: 16px; }
  .mb-4 { margin-bottom: 16px; }
  .flex { display: flex; } .items-center { align-items: center; } .justify-between { justify-content: space-between; }
  .gap-2 { gap: 8px; } .gap-3 { gap: 12px; }
  .font-bold { font-weight: 700; } .font-semibold { font-weight: 600; } .w-full { width: 100%; }
  .mb-1 { margin-bottom: 4px; }

  /* ── RESPONSIVE ── */

  /* ── MOBILE CARD LIST ── */
  .mobile-list { display: none; flex-direction: column; gap: 10px; }
  .m-card { background: var(--card); border-radius: 14px; border: 1px solid var(--border); box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden; }
  .m-card-row { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 0 16px; }
  .m-card-row .text-muted { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 60%; }
  .m-card-content { padding: 14px 0 10px; }
  .m-card-body { padding: 14px 16px 10px; }
  .m-card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6px; }
  .m-card-title { font-size: 15px; font-weight: 700; color: var(--ink); }
  .m-card-ref { font-size: 11px; color: #9ca3af; margin-top: 2px; }
  .m-card-meta { display: flex; gap: 14px; margin-top: 8px; flex-wrap: wrap; }
  .m-card-meta span { font-size: 12.5px; color: #6b7280; display: flex; align-items: center; gap: 4px; }
  .m-card-meta strong { color: #374151; font-weight: 600; }
  .m-card-value { font-family: var(--font-mono); font-size: 17px; font-weight: 600; color: var(--ink); margin-top: 8px; }
  .m-card-avatar { width: 40px; height: 40px; background: linear-gradient(135deg,var(--ink),var(--ink-light)); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: var(--gold-light); font-weight: 700; font-size: 15px; flex-shrink: 0; }
  .m-actions { display: flex; border-top: 1px solid var(--border); background: var(--off); }
  .m-btn { flex: 1; padding: 11px 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; border: none; background: transparent; cursor: pointer; border-right: 1px solid var(--border); transition: background 0.12s; }
  .m-btn:last-child { border-right: none; }
  .m-btn:hover { background: rgba(200,151,42,0.07); }
  .m-btn svg { width: 18px; height: 18px; color: var(--muted); }
  .m-btn span { font-size: 9px; font-weight: 700; color: var(--muted); letter-spacing: 0.4px; text-transform: uppercase; }
  .m-btn.m-btn-edit svg { color: var(--ink); }
  .m-btn.m-btn-email svg { color: #3b82f6; }
  .m-btn.m-btn-whatsapp svg { color: #25d366; }
  .m-btn.m-btn-download svg { color: var(--status-green-fg); }
  .m-btn.m-btn-delete svg { color: #ef4444; }
  .m-btn.m-btn-quote { flex: 1.4; }
  .m-btn.m-btn-quote span { color: var(--status-green-fg); font-size: 10px; font-weight: 700; }
  .m-btn.m-btn-view svg { color: var(--gold); }
  .m-search-row { display: flex; gap: 8px; margin-bottom: 14px; align-items: center; }
  .m-count { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: #9ca3af; margin-bottom: 8px; }
  @media (max-width: 768px) {
    .desktop-table { display: none !important; }
    .mobile-list { display: flex !important; }
    .m-search-row .search-bar { flex: 1; }
  }
  /* Graph card header */
  .graph-card { padding: 14px 18px 16px; }
  .graph-card-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:14px; }
  .graph-card-title { display:flex; align-items:center; gap:9px; font-weight:700; font-size:13px; color:var(--ink); }
  .graph-icon {
    width:28px; height:28px; border-radius:8px; flex-shrink:0;
    display:flex; align-items:center; justify-content:center;
    background: linear-gradient(145deg, rgba(200,151,42,0.22), rgba(200,151,42,0.08));
    border: 1px solid rgba(200,151,42,0.35);
    box-shadow: 0 1px 6px rgba(200,151,42,0.1), inset 0 1px 0 rgba(255,255,255,0.15);
    position:relative; overflow:hidden;
  }
  .graph-icon::after { content:''; position:absolute; top:0; left:0; right:0; height:50%; background:linear-gradient(rgba(255,255,255,0.12),transparent); border-radius:8px 8px 0 0; }
  .graph-icon svg { width:13px; height:13px; position:relative; z-index:1; }
  .graph-subtitle { font-size:11px; color:#94a3b8; font-weight:500; }

  /* Mobile-only elements — hidden by default on desktop */
  .bottom-nav { display: none; }
  .mobile-header { display: none; }
  .quick-actions-row { display: none; }

  @media (min-width: 769px) and (min-height: 500px) {
    .mobile-list { display: none !important; }
    .desktop-table { display: block; }
  }


  /* ── SETTINGS MOBILE NAV GRID ── */
  .settings-mobile-nav { display: none; }
  .settings-nav-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1px; background: #1e2d3d; }
  .settings-nav-tile { background: #0f1923; padding: 13px 6px 11px; display: flex; flex-direction: column; align-items: center; gap: 5px; cursor: pointer; position: relative; transition: background 0.15s; }
  .settings-nav-tile.active { background: #141f2b; }
  .settings-nav-tile.active::after { content:''; position:absolute; bottom:0; left:20%; right:20%; height:2.5px; background:var(--gold); border-radius:2px 2px 0 0; }
  .settings-nav-tile-icon { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; background:rgba(255,255,255,0.05); transition:background 0.15s; }
  .settings-nav-tile.active .settings-nav-tile-icon { background: rgba(200,151,42,0.14); }
  .settings-nav-tile-label { font-size:9.5px; font-weight:600; color:rgba(255,255,255,0.32); letter-spacing:0.3px; text-align:center; transition:color 0.15s; }
  .settings-nav-tile.active .settings-nav-tile-label { color: var(--gold); }
  .settings-sec-header { display:flex; align-items:center; gap:10px; margin-bottom:16px; }
  .settings-sec-icon { width:36px; height:36px; background:var(--ink); border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; box-shadow:0 2px 8px rgba(0,0,0,0.15); }
  .settings-logo-card { background:var(--ink); border-radius:14px; padding:16px; margin-bottom:12px; display:flex; align-items:center; gap:14px; box-shadow:0 2px 12px rgba(0,0,0,0.12); }
  .settings-logo-preview { width:58px; height:58px; border-radius:12px; background:rgba(200,151,42,0.08); border:2px dashed rgba(200,151,42,0.28); display:flex; align-items:center; justify-content:center; flex-shrink:0; overflow:hidden; }
  .settings-logo-btn { background:rgba(200,151,42,0.14); border:1px solid rgba(200,151,42,0.32); color:var(--gold); border-radius:9px; padding:8px 14px; font-size:11.5px; font-weight:700; cursor:pointer; white-space:nowrap; letter-spacing:0.2px; transition:background 0.15s; }
  .settings-logo-btn:hover { background:rgba(200,151,42,0.22); }
  .settings-support-card { background:white; border-radius:14px; overflow:hidden; border:1px solid var(--border); margin-bottom:12px; box-shadow:0 1px 6px rgba(0,0,0,0.04); }
  .settings-support-row { display:flex; align-items:center; gap:13px; padding:14px 15px; border-bottom:1px solid #f1f5f9; cursor:pointer; text-decoration:none; color:inherit; transition:background 0.12s; }
  .settings-support-row:last-child { border-bottom:none; }
  .settings-support-row:hover { background:#fafbfc; }
  .settings-support-row:active { background:#f4f5f7; }
  .settings-support-icon { width:40px; height:40px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:19px; flex-shrink:0; }
  .settings-version-card { background:var(--ink); border-radius:14px; padding:18px 16px; display:flex; align-items:center; justify-content:space-between; box-shadow:0 2px 12px rgba(0,0,0,0.12); }
  .settings-version-desktop { display:none; align-items:center; padding:12px 0 4px; border-top:1px solid var(--border-light); margin-top:8px; }
  .settings-plan-dark { background:var(--ink); border-radius:14px; padding:18px 16px; margin-bottom:12px; box-shadow:0 2px 12px rgba(0,0,0,0.12); }
  .settings-mobile-topbar { background:var(--ink); padding:14px 16px 12px; margin:-16px -14px 0; display:flex; align-items:center; justify-content:space-between; }
  .settings-mobile-topbar-badge { font-size:9px; font-weight:700; letter-spacing:1px; text-transform:uppercase; color:var(--gold); background:rgba(200,151,42,0.12); border:1px solid rgba(200,151,42,0.25); border-radius:20px; padding:2px 9px; }
  @media (max-width: 768px) {
    .settings-mobile-nav { display: block; margin:-16px -14px 0; }
    .settings-mobile-topbar { display:flex; }
    .settings-desktop-tabs { display: none !important; }
    .settings-content-wrap { padding-top: 0; }
  }
  @media (min-width: 769px) {
    .settings-nav-grid { display: none; }
    .settings-logo-card { display: none; }
    .settings-mobile-topbar { display:none !important; }
    .settings-sec-header { display: none; }
    .settings-version-card { display: none; }
    .settings-version-desktop { display: flex; }
  }
  @media (max-width: 768px) {
    .sidebar { display: none !important; }
    .main { margin-left: 0; padding-bottom: 80px; overflow-x: hidden; display: flex; flex-direction: column; width: 100%; max-width: 100vw; }
    .topbar { display: none; }
    .mobile-header { display: block; }
    .quick-actions-row { display: block; }
    .mobile-scroll-hint { display: inline; }
    .bottom-nav { display: flex; }
    .hero { padding: 60px 20px; }
    .hero h1 { font-size: 34px; }
    .landing-nav { padding: 14px 20px; }
    /* Hide desktop KPI stat cards on mobile — already shown in KPI strip */
    .stats-grid { display: none !important; }
    /* Stack 3-col chart grids to single column on mobile */
    .dash-charts-3col { grid-template-columns: 1fr !important; }
    /* Stack 2-col grids on mobile */
    .dash-charts-2col { grid-template-columns: 1fr !important; }
    .landing-nav-links { display: none; }
    .features-section, .pricing-section { padding: 56px 20px; }
    .form-grid, .form-grid-3 { grid-template-columns: 1fr; }
    .est-4col { grid-template-columns: 1fr 1fr !important; }
    .est-line-mat { grid-template-columns: 1fr 50px !important; }
    .est-line-mat > *:nth-child(3), .est-line-mat > *:nth-child(4), .est-line-mat > *:nth-child(5) { display: none; }
    .est-line-lab { grid-template-columns: 1fr 50px !important; }
    .est-line-lab > *:nth-child(3), .est-line-lab > *:nth-child(4), .est-line-lab > *:nth-child(5) { display: none; }
    .compliance-desktop-table { display: none; }
    .compliance-mobile-cards { display: block; }
    .expense-desktop-table { display: none; }
    .expense-mobile-cards { display: block; }
    .expense-mobile-summary { display: block; }
    .expense-desktop-sidebar { display: none; }
    .inventory-desktop-table { display: none; }
    .inventory-mobile-cards { display: block; }
    .cal-desktop-view { display: none; }
    .cal-mobile-view { display: block; }
    .cal-view-toggle { display: flex; }
    .stats-grid { grid-template-columns: 1fr 1fr; }
    .page { padding: 12px 14px; overflow-x: hidden; max-width: 100%; }
    .main { overflow-x: hidden; }
    .dashboard-main-grid { grid-template-columns: 1fr !important; }
    /* Dashboard graph rows — collapse to single column on mobile */
    .dash-row-3col > *, .dash-row-2col > *, .dashboard-main-grid > * { min-width: 0; }
    .dash-row-3col { grid-template-columns: 1fr !important; }
    .dash-row-2col { grid-template-columns: 1fr !important; }
    /* Fix stats grid on mobile */
    .stats-grid { grid-template-columns: 1fr 1fr !important; }
    .cashflow-grid { grid-template-columns: 1fr !important; }
    table th, table td { padding: 8px 10px; font-size: 12px; }
    .topbar { padding: 0 14px; }
    .topbar h2 { font-size: 14px; }
    .modal { max-height: calc(100vh - 80px); border-radius: 16px 16px 0 0; }
    .modal-overlay { align-items: flex-end; padding: 0; padding-bottom: 80px; }
    .modal-footer { padding-bottom: 20px; }
    .line-item-row { grid-template-columns: 1fr 60px 80px 80px 32px; gap: 4px; }
    .doc-preview { padding: 14px; }
    .doc-header { flex-direction: column; gap: 12px; }
    .doc-number { text-align: left; }
    .doc-meta { grid-template-columns: 1fr; }
  }
  @media (max-width: 480px) {
    .stats-grid { grid-template-columns: 1fr; }
    .hero-buttons { flex-direction: column; }
    .btn-hero-primary, .btn-hero-outline { width: 100%; text-align: center; justify-content: center; }
  }
`;

// ─── HELPERS ──────────────────────────────────────────────────────────────────
// ─── INDEXED DB SESSION ────────────────────────────────────────────────────────
const IDB = {
  DB_NAME: "fieldjob_db",
  STORE: "session",

  open: () => new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB.DB_NAME, 1);
    req.onupgradeneeded = e => {
      e.target.result.createObjectStore(IDB.STORE, { keyPath: "key" });
    };
    req.onsuccess = e => resolve(e.target.result);
    req.onerror = () => reject(req.error);
  }),

  set: async (key, value) => {
    try {
      const db = await IDB.open();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB.STORE, "readwrite");
        tx.objectStore(IDB.STORE).put({ key, value });
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => reject(tx.error);
      });
    } catch { return false; }
  },

  get: async (key) => {
    try {
      const db = await IDB.open();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB.STORE, "readonly");
        const req = tx.objectStore(IDB.STORE).get(key);
        req.onsuccess = () => resolve(req.result ? req.result.value : null);
        req.onerror = () => reject(req.error);
      });
    } catch { return null; }
  },

  remove: async (key) => {
    try {
      const db = await IDB.open();
      return new Promise((resolve) => {
        const tx = db.transaction(IDB.STORE, "readwrite");
        tx.objectStore(IDB.STORE).delete(key);
        tx.oncomplete = () => resolve(true);
      });
    } catch { return false; }
  },
};


// ─── CURRENCY & TAX SYSTEM ───────────────────────────────────────────────────
const CURRENCY_OPTIONS = [
  { code:"ZAR", symbol:"R",   name:"South African Rand",  locale:"en-ZA", taxLabel:"VAT",        taxRate:15, flag:"🇿🇦" },
  { code:"USD", symbol:"$",   name:"US Dollar",           locale:"en-US", taxLabel:"Sales Tax",  taxRate:0,  flag:"🇺🇸", customRate:true },
  { code:"GBP", symbol:"£",   name:"British Pound",       locale:"en-GB", taxLabel:"VAT",        taxRate:20, flag:"🇬🇧" },
  { code:"EUR", symbol:"€",   name:"Euro",                locale:"de-DE", taxLabel:"VAT",        taxRate:20, flag:"🇪🇺" },
  { code:"AUD", symbol:"A$",  name:"Australian Dollar",   locale:"en-AU", taxLabel:"GST",        taxRate:10, flag:"🇦🇺" },
  { code:"CAD", symbol:"C$",  name:"Canadian Dollar",     locale:"en-CA", taxLabel:"GST/HST",    taxRate:5,  flag:"🇨🇦", customRate:true },
  { code:"INR", symbol:"₹",   name:"Indian Rupee",        locale:"en-IN", taxLabel:"GST",        taxRate:18, flag:"🇮🇳" },
  { code:"SGD", symbol:"S$",  name:"Singapore Dollar",    locale:"en-SG", taxLabel:"GST",        taxRate:9,  flag:"🇸🇬" },
];

function getCurrencyOption(code) {
  return CURRENCY_OPTIONS.find(c => c.code === code) || CURRENCY_OPTIONS[0];
}

function formatCurrency(amount, currency) {
  const opt = getCurrencyOption(currency || "ZAR");
  const num = Number(amount || 0);
  const formatted = num.toLocaleString(opt.locale, { minimumFractionDigits:2, maximumFractionDigits:2 });
  return `${opt.symbol} ${formatted}`;
}

// Keep formatZAR as alias for backward compat — uses business currency if available
const formatZAR = (amount, currency) => formatCurrency(amount, currency || "ZAR");
const formatDate = (d) => { if (!d) return ""; const dt = new Date(d); return `${String(dt.getDate()).padStart(2,"0")}/${String(dt.getMonth()+1).padStart(2,"0")}/${dt.getFullYear()}`; };
const today = () => new Date().toISOString().split("T")[0];
const addDays = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt.toISOString().split("T")[0]; };
const daysLeft = (expiry) => { const diff = new Date(expiry) - new Date(); return Math.ceil(diff / 86400000); };



const STATUS_COLORS = {
  "Pending": "badge-gray", "Scheduled": "badge-blue", "In Progress": "badge-amber",
  "Awaiting Parts": "badge-purple", "Completed": "badge-green",
  "Invoiced": "badge-gold", "Paid": "badge-green",
};
const STATUS_PIPELINE = ["Pending","Scheduled","In Progress","Awaiting Parts","Completed","Invoiced","Paid"];

// ─── ICONS ───────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 16, ...props }) => {
  const icons = {
    dashboard: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
    jobs: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="2"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="12" y2="16"/></svg>,
    calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    quote: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
    invoice: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
    workers: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
    clients: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    cashflow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    settings: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M19.07 19.07l-1.41-1.41M4.93 19.07l1.41-1.41M12 2v2M12 20v2M2 12h2M20 12h2"/></svg>,
    logout: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
    plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
    edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
    eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    eyeOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>,
    download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
    x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><polyline points="20 6 9 17 4 12"/></svg>,
    whatsapp: <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size} {...props}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>,
    menu: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
    receipt: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="16" y2="14"/></svg>,
    photo: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
    alert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    email: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    inventory: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
    expenses: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
    compliance: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    fuel: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M3 22V8l9-6 9 6v14"/><path d="M9 22V12h6v10"/></svg>,
    warning: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    upload: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/></svg>,
    file: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>,
    chevron_right: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><polyline points="9 18 15 12 9 6"/></svg>,
    chevron_left: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><polyline points="15 18 9 12 15 6"/></svg>,
    building: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>,
    currency: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 100 4h4a2 2 0 110 4H8"/><line x1="12" y1="6" x2="12" y2="18"/></svg>,
    estimate: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="2" y1="9" x2="22" y2="9"/><line x1="7" y1="3" x2="7" y2="9"/><line x1="7" y1="13" x2="10" y2="13"/><line x1="7" y1="17" x2="10" y2="17"/><line x1="14" y1="13" x2="17" y2="13"/><line x1="14" y1="17" x2="17" y2="17"/></svg>,
    portal: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/><line x1="19" y1="8" x2="23" y2="8"/><line x1="21" y1="6" x2="21" y2="10"/></svg>,
    report: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/><circle cx="19" cy="19" r="3" fill="none"/><path d="M21.5 21.5l-1.5-1.5"/></svg>,
    weather: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
    hammer: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M9 3L5 7l10 10 4-4L9 3z"/><path d="M5 7L3 9l4 4 2-2-4-4z"/><line x1="5" y1="19" x2="9" y2="15"/></svg>,
    hardhat: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M2 18a1 1 0 001 1h18a1 1 0 001-1v-2a1 1 0 00-1-1H3a1 1 0 00-1 1v2z"/><path d="M10 10V5a2 2 0 114 0v5"/><path d="M4 15a8 8 0 0116 0"/></svg>,
    crane: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><line x1="4" y1="20" x2="4" y2="4"/><line x1="4" y1="4" x2="20" y2="4"/><line x1="20" y1="4" x2="20" y2="10"/><line x1="4" y1="10" x2="20" y2="10"/><line x1="12" y1="4" x2="4" y2="20"/><circle cx="20" cy="14" r="2"/><line x1="20" y1="16" x2="20" y2="20"/></svg>,
    box: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
    shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>,
    target: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
    clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    pen: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>,
    upgrade: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><polyline points="17 11 12 6 7 11"/><polyline points="17 18 12 13 7 18"/></svg>,
    tools: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>,
    external: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} {...props}><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  };
  return icons[name] || <span>{name}</span>;
};

// ─── APP STATE / CONTEXT ─────────────────────────────────────────────────────
const AppContext = createContext({});
const useApp = () => useContext(AppContext);

// ─── GLOBAL DELETE CONFIRM MODAL ─────────────────────────────────────────────
function DeleteConfirmModal({ item, onConfirm, onCancel }) {
  if (!item) return null;
  return (
    <div className="modal-overlay" onClick={onCancel} style={{ zIndex: 9999 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:"white", borderRadius:16, maxWidth:400, width:"calc(100% - 40px)",
        margin:"0 auto", overflow:"hidden",
        boxShadow:"0 20px 60px rgba(15,25,35,0.18), 0 4px 16px rgba(15,25,35,0.10)"
      }}>
        {/* Red top bar */}
        <div style={{ height:4, background:"linear-gradient(90deg,#dc2626,#ef4444)" }}/>
        <div style={{ padding:"28px 28px 24px", textAlign:"center" }}>
          {/* Icon */}
          <div style={{
            width:56, height:56, borderRadius:14, background:"#fee2e2",
            display:"flex", alignItems:"center", justifyContent:"center",
            margin:"0 auto 18px",
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
            </svg>
          </div>
          <div style={{ fontSize:17, fontWeight:700, color:"#0f1923", marginBottom:8, letterSpacing:"-0.2px" }}>
            Delete {item}?
          </div>
          <div style={{ fontSize:13, color:"#64748b", lineHeight:1.6, marginBottom:24 }}>
            This cannot be undone. Once deleted, this record will be permanently removed.
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={onCancel} style={{
              flex:1, padding:"10px 0", borderRadius:9, border:"1px solid #e4e9f0",
              background:"white", color:"#374151", fontSize:13, fontWeight:600, cursor:"pointer",
              fontFamily:"var(--font)", transition:"background 0.15s"
            }}>Cancel</button>
            <button onClick={onConfirm} style={{
              flex:1, padding:"10px 0", borderRadius:9, border:"none",
              background:"#dc2626", color:"white", fontSize:13, fontWeight:700, cursor:"pointer",
              fontFamily:"var(--font)", boxShadow:"0 2px 8px rgba(220,38,38,0.3)"
            }}>Delete</button>
          </div>
        </div>
      </div>
    </div>
  );
}
// deleteConfirm state shape: { id, label, table } — no functions stored in state
// Each module handles the actual delete in its own onConfirm handler

// ─── PASSWORD INPUT WITH SHOW/HIDE TOGGLE ────────────────────────────────────
function PasswordInput({ value, onChange, placeholder, autoFocus, className }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        className={className || "form-input"}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder || "••••••••"}
        autoFocus={autoFocus}
        style={{ paddingRight: 42 }}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        style={{
          position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
          background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0,
          display: "flex", alignItems: "center",
        }}
        tabIndex={-1}
      >
        <Icon name={show ? "eyeOff" : "eye"} size={16} />
      </button>
    </div>
  );
}


// ─── UNIT SYSTEM ─────────────────────────────────────────────────────────────
const UNIT_LABELS = {
  metric:   { km:"km", miles:"km", m:"m", ft:"m", m2:"m²", ft2:"m²", m3:"m³", ft3:"m³", kg:"kg", lbs:"kg", L:"L", gal:"L", ml:"ml", floz:"ml", mm:"mm", inch:"mm", kmh:"km/h", mph:"km/h", rateKm:"rate/km" },
  imperial: { km:"miles", miles:"miles", m:"ft", ft:"ft", m2:"ft²", ft2:"ft²", m3:"ft³", ft3:"ft³", kg:"lbs", lbs:"lbs", L:"gal", gal:"gal", ml:"fl oz", floz:"fl oz", mm:"in", inch:"in", kmh:"mph", mph:"mph", rateKm:"rate/mile" },
};

function unitLabel(metricKey, unitSystem) {
  const sys = unitSystem || "metric";
  return UNIT_LABELS[sys]?.[metricKey] || metricKey;
}

// ─── RETRY HELPER ────────────────────────────────────────────────────────────
// Retries failed fetch calls with exponential backoff on 5xx/network errors
async function fetchWithRetry(url, options = {}, retries = 3) {
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url, options);
      // Only retry on server errors, not client errors (4xx)
      if (res.status >= 500 && i < retries - 1) {
        await new Promise(r => setTimeout(r, Math.pow(2, i) * 500));
        continue;
      }
      return res;
    } catch (e) {
      // Network failure — retry with backoff
      if (i < retries - 1) {
        await new Promise(r => setTimeout(r, Math.pow(2, i) * 500));
        continue;
      }
      throw e;
    }
  }
}

// ─── SUPABASE DB LAYER ────────────────────────────────────────────────────────
const TABLE_MAP = {
  jobs: "fj_jobs", quotes: "fj_quotes", invoices: "fj_invoices",
  receipts: "fj_receipts", clients: "fj_clients", workers: "fj_workers",
  expenses: "fj_expenses", inventory: "fj_inventory", suppliers: "fj_suppliers",
  purchases: "fj_purchases", compliance: "fj_compliance", business: "fj_business",
  users: "fj_users", demo_requests: "fj_demo_requests",
  estimates: "fj_estimates",
  reports:   "fj_reports",
};

// ─── LOCAL DATA CACHE ─────────────────────────────────────────────────────────
// Wraps localStorage to persist Supabase data for offline use.
// Every successful fetch writes to cache. Every failed fetch reads from cache.
// ─── DATA CACHE (IndexedDB — replaces localStorage for larger datasets) ──────
// Uses IDB which has no size limit vs localStorage's 5-10MB cap.
// Falls back to localStorage read for backward compatibility on first load.
const DataCache = {
  key: (table, userId) => `fj_cache_${table}_${userId}`,

  save: async (table, userId, data) => {
    const key = DataCache.key(table, userId);
    try {
      await IDB.set(key, JSON.stringify({ data, at: Date.now() }));
    } catch {
      // Fallback to localStorage if IDB fails
      try { localStorage.setItem(key, JSON.stringify({ data, at: Date.now() })); } catch {}
    }
  },

  load: async (table, userId) => {
    const key = DataCache.key(table, userId);
    try {
      const val = await IDB.get(key);
      if (val) return JSON.parse(val).data;
      // Migrate from old localStorage cache on first load
      const lsRaw = localStorage.getItem(key);
      if (lsRaw) { localStorage.removeItem(key); return JSON.parse(lsRaw).data; }
      return null;
    } catch { return null; }
  },

  saveOne: async (table, userId, data) => {
    const key = DataCache.key(table + "_one", userId);
    try {
      await IDB.set(key, JSON.stringify({ data, at: Date.now() }));
    } catch {
      try { localStorage.setItem(key, JSON.stringify({ data, at: Date.now() })); } catch {}
    }
  },

  loadOne: async (table, userId) => {
    const key = DataCache.key(table + "_one", userId);
    try {
      const val = await IDB.get(key);
      if (val) return JSON.parse(val).data;
      const lsRaw = localStorage.getItem(key);
      if (lsRaw) { localStorage.removeItem(key); return JSON.parse(lsRaw).data; }
      return null;
    } catch { return null; }
  },
};

const DB = {
  // For owner account (no JWT): use anon key directly.
  // For regular users: use JWT token so RLS auth.uid() check works.
  // Owner data is protected by querying with user_id="owner_admin" — 
  // no other user knows or uses that id.
  _h: () => {
    const stored = (() => { try { return JSON.parse(localStorage.getItem("fj_user")); } catch { return null; } })();
    const isOwnerSession = stored?.id === "owner_admin";
    const token = isOwnerSession ? null : localStorage.getItem("sb_token");
    return {
      "Content-Type": "application/json",
      "apikey": SUPABASE_ANON_KEY,
      "Authorization": token ? `Bearer ${token}` : `Bearer ${SUPABASE_ANON_KEY}`,
    };
  },
  // Get list of records for a user — caches to localStorage, returns cache when offline
  getList: async (table, userId) => {
    try {
      const t = TABLE_MAP[table] || table;
      const url = `${SUPABASE_URL}/rest/v1/${t}?user_id=eq.${encodeURIComponent(userId)}&order=created_at.desc&limit=200`;
      const res = await fetchWithRetry(url, { headers: DB._h() });
      if (!res.ok) {
        // Network responded but with error — return cache
        return (await DataCache.load(table, userId)) || [];
      }
      const data = await res.json();
      // Save fresh data to cache
      DataCache.save(table, userId, data);
      return data;
    } catch {
      // Network failed (offline) — return cached data
      return (await DataCache.load(table, userId)) || [];
    }
  },

  // Get single record (e.g. business profile) — caches to localStorage
  get: async (table, userId) => {
    try {
      const t = TABLE_MAP[table] || table;
      const url = `${SUPABASE_URL}/rest/v1/${t}?user_id=eq.${encodeURIComponent(userId)}&limit=1`;
      const res = await fetchWithRetry(url, { headers: DB._h() });
      if (!res.ok) {
        return await DataCache.loadOne(table, userId);
      }
      const data = await res.json();
      const record = data[0] || null;
      if (record) DataCache.saveOne(table, userId, record);
      return record;
    } catch {
      return await DataCache.loadOne(table, userId);
    }
  },

  // Insert new record
  insert: async (table, data) => {
    try {
      const t = TABLE_MAP[table] || table;
      const record = { ...data, created_at: data.created_at || new Date().toISOString() };
      if (!record.id) delete record.id;
      const res = await fetchWithRetry(`${SUPABASE_URL}/rest/v1/${t}`, {
        method: "POST",
        headers: { ...DB._h(), "Prefer": "return=representation" },
        body: JSON.stringify(record),
      });
      if (!res.ok) {
        const errText = await res.text();
        console.error(`DB.insert [${t}] error:`, errText);
        throw new Error(errText);
      }
      const result = await res.json();
      return Array.isArray(result) ? result[0] : result;
    } catch(e) { console.error("DB.insert:", e); return null; }
  },

  // Update existing record by id
  update: async (table, id, data) => {
    try {
      const t = TABLE_MAP[table] || table;
      const record = { ...data, updated_at: new Date().toISOString() };
      const res = await fetchWithRetry(`${SUPABASE_URL}/rest/v1/${t}?id=eq.${id}`, {
        method: "PATCH",
        headers: { ...DB._h(), "Prefer": "return=representation" },
        body: JSON.stringify(record),
      });
      if (!res.ok) { const e = await res.text(); console.error("DB.update error:", e); throw new Error(e); }
      const result = await res.json();
      return Array.isArray(result) ? result[0] : result;
    } catch(e) { console.error("DB.update:", e); return null; }
  },

  // Upsert (insert or update based on user_id for single-record tables like business)
  upsert: async (table, data) => {
    try {
      const t = TABLE_MAP[table] || table;
      const record = { ...data, updated_at: new Date().toISOString() };
      const res = await fetchWithRetry(`${SUPABASE_URL}/rest/v1/${t}`, {
        method: "POST",
        headers: { ...DB._h(), "Prefer": "resolution=merge-duplicates,return=representation" },
        body: JSON.stringify(record),
      });
      if (!res.ok) { const e = await res.text(); console.error("DB.upsert error:", e); throw new Error(e); }
      const result = await res.json();
      return Array.isArray(result) ? result[0] : result;
    } catch(e) { console.error("DB.upsert:", e); return null; }
  },

  // Delete record by id
  remove: async (table, id) => {
    try {
      const t = TABLE_MAP[table] || table;
      await fetch(`${SUPABASE_URL}/rest/v1/${t}?id=eq.${id}`, {
        method: "DELETE",
        headers: DB._h(),
      });
      return true;
    } catch(e) { console.error("DB.remove:", e); return false; }
  },

  // Get user record by email
  getUserByEmail: async (email) => {
    try {
      const url = `${SUPABASE_URL}/rest/v1/fj_users?email=eq.${encodeURIComponent(email)}&limit=1`;
      const res = await fetch(url, { headers: DB._h() });
      if (!res.ok) return null;
      const data = await res.json();
      return data[0] || null;
    } catch { return null; }
  },

  // Get all demo requests (admin)
  getDemoRequests: async () => {
    try {
      const url = `${SUPABASE_URL}/rest/v1/fj_demo_requests?order=created_at.desc`;
      const res = await fetch(url, { headers: DB._h() });
      if (!res.ok) return [];
      return await res.json();
    } catch { return []; }
  },

  // Get all users (admin)
  getAllUsers: async () => {
    try {
      const url = `${SUPABASE_URL}/rest/v1/fj_users?order=created_at.desc`;
      const res = await fetch(url, { headers: DB._h() });
      if (!res.ok) return [];
      return await res.json();
    } catch { return []; }
  },
};


// ─── PDF PRINT UTILITIES ─────────────────────────────────────────────────────
function printAsPDF(htmlContent, title) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) { alert("Please allow popups for this site to enable PDF printing."); return; }
  // Write the full document HTML as-is — buildEstimateDocHTML / buildDocHTML
  // already produce complete styled output. We just need a clean window + print trigger.
  printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>${title}</title>
  <style>
    @media print {
      body { margin: 0; }
      .no-print { display: none !important; }
    }
    .no-print {
      position: fixed; bottom: 24px; right: 24px;
      background: #c8972a; color: white; border: none;
      padding: 14px 28px; border-radius: 10px;
      font-size: 15px; font-weight: 700; cursor: pointer;
      box-shadow: 0 4px 20px rgba(0,0,0,0.2); z-index: 999;
      font-family: 'Segoe UI', Arial, sans-serif;
    }
    .no-print:hover { background: #a67820; }
  </style>
</head>
<body>
  <button class="no-print" onclick="window.print()">🖨️ Save as PDF</button>
  ${htmlContent}
</body>
</html>`);
  printWindow.document.close();
}


// ─── PDF TEMPLATE ENGINE ────────────────────────────────────────────────────
// Helper: shared data prep for quote/invoice
function _docData(doc, type, business) {
  const isInvoice = type === "Invoice";
  const outstanding = (doc.total||0) - (doc.paid||0);
  const logoHtml = business.logo ? `<img src="${business.logo}" style="max-width:110px;max-height:65px;object-fit:contain;display:block;margin-bottom:6px;" alt="logo"loading="lazy" />` : "";
  const items = (doc.items||[]).map(item => `
    <tr>
      <td>${item.description||""}</td>
      <td style="text-align:right">${item.qty||0}</td>
      <td style="text-align:right">R ${parseFloat(item.unit_price||0).toFixed(2)}</td>
      <td style="text-align:right"><strong>R ${((item.qty||0)*(item.unit_price||0)).toFixed(2)}</strong></td>
    </tr>`).join("");
  const curr = business.currency || "ZAR";
  const taxLbl = business.tax_label || getCurrencyOption(curr).taxLabel || "VAT";
  const taxRate = business.tax_rate !== undefined ? business.tax_rate : getCurrencyOption(curr).taxRate;
  const fmt = (v) => formatCurrency(v, curr);
  const totalsHtml = `
    ${(doc.discount>0) ? `<div style="font-size:13px;color:#64748b">Line Total: ${fmt(doc.line_total||doc.subtotal||0)}</div>` : ""}
    ${(doc.discount>0) ? `<div style="font-size:13px;color:#16a34a;margin-top:4px">Discount (${doc.discount}%): -${fmt(doc.discount_amt||0)}</div>` : ""}
    <div style="font-size:13px;color:#64748b">Subtotal: ${fmt(doc.subtotal||0)}</div>
    ${(doc.vat>0) ? `<div style="font-size:13px;color:#64748b;margin-top:4px">${taxLbl}${taxRate>0?` (${taxRate}%)`:"" }: ${fmt(doc.vat||0)}</div>` : ""}
    <div style="font-size:22px;font-weight:700;margin-top:6px">Total: ${fmt(doc.total||0)}</div>
    ${isInvoice && doc.paid>0 ? `<div style="font-size:13px;color:#10b981;margin-top:6px">Paid: ${fmt(doc.paid||0)}</div><div style="font-size:15px;font-weight:700;color:#ef4444">Outstanding: ${fmt(outstanding||0)}</div>` : ""}`;
  const bankingHtml = isInvoice && business.bank_name ? `
    <div style="margin-top:20px;padding:16px;border-radius:8px;border-left:4px solid #c8972a;background:#f8fafc;">
      <div style="font-weight:700;font-size:13px;margin-bottom:10px;color:#c8972a">Banking Details — Ref: ${doc.number}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:12.5px;">
        <div><span style="color:#94a3b8">Bank: </span><strong>${business.bank_name}</strong></div>
        <div><span style="color:#94a3b8">Account Holder: </span><strong>${business.bank_account_holder||""}</strong></div>
        <div><span style="color:#94a3b8">Account No: </span><strong>${business.bank_account_number||""}</strong></div>
        <div><span style="color:#94a3b8">Branch: </span><strong>${business.bank_branch_code||""}</strong></div>
      </div>
    </div>` : "";
  // Payment link buttons on invoice PDF
  const payLinks = (() => {
    if (!isInvoice || doc.status === "Paid") return "";
    const btns = [];
    if (business.payfast_link) btns.push(`<a href="${business.payfast_link}" style="display:inline-block;background:#007bff;color:white;padding:9px 18px;border-radius:6px;font-weight:700;font-size:12px;text-decoration:none;margin-right:8px">Pay via PayFast</a>`);
    if (business.ozow_link)    btns.push(`<a href="${business.ozow_link}"    style="display:inline-block;background:#6c2bd9;color:white;padding:9px 18px;border-radius:6px;font-weight:700;font-size:12px;text-decoration:none;margin-right:8px">Pay via Ozow</a>`);
    if (business.other_payment_link) btns.push(`<a href="${business.other_payment_link}" style="display:inline-block;background:#10b981;color:white;padding:9px 18px;border-radius:6px;font-weight:700;font-size:12px;text-decoration:none;margin-right:8px">${business.other_payment_label||"Pay Online"}</a>`);
    if (!btns.length) return "";
    return `<div style="margin-top:16px;padding:16px;border-radius:8px;background:#f0fdf4;border-left:4px solid #10b981;">
      <div style="font-weight:700;font-size:13px;margin-bottom:10px;color:#065f46">Pay Online</div>
      <div>${btns.join("")}</div>
    </div>`;
  })();
  const notesHtml = doc.notes ? `<div style="padding:12px;background:#fffbeb;border-radius:8px;font-size:12.5px;color:#92400e;border-left:3px solid #f59e0b;margin-bottom:16px"><strong>Notes:</strong> ${doc.notes}</div>` : "";
  const paidStamp = isInvoice && doc.status==="Paid" ? `<div style="margin-top:8px"><span style="background:#10b981;color:white;padding:4px 14px;border-radius:20px;font-weight:700;font-size:12px">✓ PAID IN FULL</span></div>` : "";
  const dateLabel = isInvoice
    ? `<div style="font-size:12px;color:${doc.due_date < new Date().toISOString().slice(0,10) && doc.status!=="Paid" ? "#ef4444" : "#64748b"}">Due: ${formatDate(doc.due_date)}</div>`
    : `<div style="font-size:12px;color:#64748b">Valid Until: ${formatDate(doc.expiry_date)}</div>`;
  const bizInfo = `
    ${business.address ? `<div>${business.address}</div>` : ""}
    ${business.phone ? `<div>Tel: ${business.phone}</div>` : ""}
    ${business.email ? `<div>${business.email}</div>` : ""}
    ${business.vat_number ? `<div>Tax No: ${business.vat_number}</div>` : ""}`;
  const tableBody = `<table style="width:100%;border-collapse:collapse;margin-bottom:16px">
    <thead><tr class="thead-row">
      <th style="padding:10px 14px;text-align:left">Description</th>
      <th style="padding:10px 14px;text-align:right;width:60px">Qty</th>
      <th style="padding:10px 14px;text-align:right;width:120px">Unit Price</th>
      <th style="padding:10px 14px;text-align:right;width:120px">Amount</th>
    </tr></thead>
    <tbody class="tbody-rows">${items}</tbody>
  </table>`;
  return { isInvoice, logoHtml, totalsHtml, bankingHtml, payLinks, notesHtml, paidStamp, dateLabel, bizInfo, tableBody, outstanding };
}

function buildDocHTML(doc, type, business) {
  const tpl = business.pdf_template || "1";
  const d = _docData(doc, type, business);
  const footer = `<div style="margin-top:40px;padding-top:16px;border-top:2px solid currentColor;opacity:0.15;font-size:11px;color:#94a3b8;text-align:center;opacity:1;border-color:#e2e8f0">Generated by FieldJob &nbsp;|&nbsp; ${business.name||""} &nbsp;|&nbsp; ${new Date().toLocaleDateString("en-ZA")}</div>`;

  // ── Template 1: Classic ──────────────────────────────────────────────────
  if (tpl === "1") return `
    <style>
      .thead-row { background:#0f1923; color:white; }
      .tbody-rows tr:nth-child(even) td { background:#fafafa; }
      .tbody-rows td { border-bottom:1px solid #f1f5f9; }
    </style>
    <div style="display:flex;justify-content:space-between;margin-bottom:20px;padding-bottom:16px;border-bottom:2px solid #e2e8f0">
      <div>${d.logoHtml}<h2 style="font-size:18px;font-weight:700;margin-bottom:6px">${business.name||"Your Business"}</h2><div style="color:#64748b;font-size:12px;line-height:1.8">${d.bizInfo}</div></div>
      <div style="text-align:right"><div style="font-size:28px;font-weight:700;color:#0f1923">${type}</div><div style="font-size:16px;color:#c8972a;font-weight:600">${doc.number||""}</div><div style="font-size:12px;color:#64748b;margin-top:6px;line-height:1.8"><div>Date: ${formatDate(doc.date)}</div>${d.dateLabel}</div></div>
    </div>
    <div style="background:#f8fafc;border-radius:8px;padding:12px 16px;margin-bottom:16px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:4px">Bill To</div><div style="font-weight:700;font-size:14px">${doc.client_name||""}</div>${d.paidStamp}</div>
    ${d.tableBody}
    <div style="text-align:right;padding:16px;background:#f8fafc;border-radius:8px;margin-bottom:16px">${d.totalsHtml}</div>
    ${d.notesHtml}${d.bankingHtml}${d.payLinks||""}${footer}`;

  // ── Template 2: Dark Header ──────────────────────────────────────────────
  if (tpl === "2") return `
    <style>
      .thead-row { background:#f1f5f9; color:#0f1923; border-bottom:2px solid #0f1923 !important; }
      .thead-row th { border-bottom:2px solid #0f1923; }
      .tbody-rows td { border-bottom:1px solid #f1f5f9; }
    </style>
    <div style="background:#0f1923;margin:-52px -60px 24px;padding:28px 60px;display:flex;justify-content:space-between;align-items:flex-start">
      <div>${d.logoHtml}<div style="font-size:18px;font-weight:700;color:white">${business.name||"Your Business"}</div><div style="font-size:11px;color:rgba(255,255,255,0.4);margin-top:6px;line-height:1.8">${d.bizInfo}</div></div>
      <div style="text-align:right"><div style="font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#c8972a">${type}</div><div style="font-size:26px;font-weight:700;color:white;margin-top:4px">${doc.number||""}</div><div style="font-size:11px;color:rgba(255,255,255,0.4);margin-top:6px;line-height:1.8"><div>Date: ${formatDate(doc.date)}</div>${d.dateLabel}</div></div>
    </div>
    <div style="display:flex;gap:12px;margin-bottom:16px">
      <div style="flex:1;background:#f8fafc;border-radius:8px;padding:12px 14px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:4px">Bill To</div><div style="font-weight:700;font-size:13px">${doc.client_name||""}</div>${d.paidStamp}</div>
      <div style="flex:1;background:#f8fafc;border-radius:8px;padding:12px 14px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:4px">Invoice Date</div><div style="font-weight:700;font-size:13px">${formatDate(doc.date)}</div></div>
    </div>
    ${d.tableBody}
    <div style="background:#0f1923;color:white;border-radius:8px;padding:14px 18px;display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
      <div style="font-size:12px;color:rgba(255,255,255,0.45)">Total Due (incl. VAT)</div>
      <div style="font-size:22px;font-weight:700;color:#c8972a">R ${parseFloat(doc.total||0).toFixed(2)}</div>
    </div>
    ${d.notesHtml}${d.bankingHtml}${d.payLinks||""}${footer}`;

  // ── Template 3: Gold Accent ──────────────────────────────────────────────
  if (tpl === "3") return `
    <style>
      .thead-row th { border-bottom:2px solid #c8972a; color:#c8972a; background:none !important; }
      .tbody-rows td { border-bottom:1px solid #fef3c7; }
    </style>
    <div style="height:4px;background:linear-gradient(90deg,#c8972a,#f0d078,#c8972a);margin:-52px -60px 24px"></div>
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px">
      <div>${d.logoHtml}<div style="font-size:18px;font-weight:700">${business.name||"Your Business"}</div><div style="color:#94a3b8;font-size:12px;margin-top:6px;line-height:1.8">${d.bizInfo}</div></div>
      <div style="text-align:right"><div style="display:inline-block;background:linear-gradient(135deg,#c8972a,#f0d078);color:#0f1923;font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;padding:4px 14px;border-radius:20px">${type}</div><div style="font-size:18px;font-weight:700;margin-top:6px">${doc.number||""}</div><div style="font-size:12px;color:#94a3b8;margin-top:4px;line-height:1.8"><div>Date: ${formatDate(doc.date)}</div>${d.dateLabel}</div></div>
    </div>
    <div style="height:1px;background:linear-gradient(90deg,rgba(200,151,42,0.2),#c8972a,rgba(200,151,42,0.2));margin:14px 0"></div>
    <div style="margin-bottom:16px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#c8972a;font-weight:700;margin-bottom:4px">Bill To</div><div style="font-weight:700;font-size:14px">${doc.client_name||""}</div>${d.paidStamp}</div>
    ${d.tableBody}
    <div style="text-align:right;margin-bottom:16px;font-size:22px;font-weight:700;color:#c8972a">Total: R ${parseFloat(doc.total||0).toFixed(2)}</div>
    ${d.notesHtml}
    ${d.bankingHtml ? d.bankingHtml.replace("background:#f8fafc", "background:#fffbeb").replace("border-left:4px solid #c8972a", "border:1px solid #fde68a;border-left:4px solid #c8972a") : ""}
    ${footer}`;

  // ── Template 4: Minimal ──────────────────────────────────────────────────
  if (tpl === "4") return `
    <style>
      .thead-row th { border-bottom:1px solid #111827; color:#111827; background:none !important; font-weight:700; }
      .tbody-rows td { border-bottom:1px solid #f3f4f6; color:#374151; }
    </style>
    <div style="margin-bottom:16px"><div style="font-size:22px;font-weight:700;letter-spacing:-0.5px">${business.name||"Your Business"}</div><div style="font-size:11px;color:#9ca3af;margin-top:3px">${d.bizInfo}</div></div>
    <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:20px;padding-bottom:12px;border-bottom:1px solid #f3f4f6">
      <div><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#d1d5db;margin-bottom:3px">Bill To</div><div style="font-weight:600;font-size:14px">${doc.client_name||""}</div><div style="font-size:11px;color:#9ca3af;margin-top:3px">Date: ${formatDate(doc.date)}</div>${d.paidStamp}</div>
      <div style="text-align:right"><div style="font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#9ca3af">${type}</div><div style="font-size:24px;font-weight:700">${doc.number||""}</div></div>
    </div>
    ${d.tableBody}
    <div style="text-align:right;font-weight:700;font-size:18px;border-top:1px solid #111827;padding-top:10px;margin-bottom:16px">Total: R ${parseFloat(doc.total||0).toFixed(2)}</div>
    ${d.notesHtml}
    ${d.bankingHtml ? d.bankingHtml.replace("background:#f8fafc", "background:none").replace("border-left:4px solid #c8972a;", "border-top:1px dashed #e5e7eb;border-left:none;") : ""}
    ${footer}`;

  // ── Template 5: Bold Dark ────────────────────────────────────────────────
  if (tpl === "5") return `
    <style>
      body,.doc-wrap { background:#0f1923 !important; }
      .thead-row th { border-bottom:1px solid rgba(200,151,42,0.4); color:#c8972a; background:none !important; }
      .tbody-rows td { border-bottom:1px solid rgba(255,255,255,0.06); color:rgba(255,255,255,0.8); }
    </style>
    <div style="background:#0f1923;color:white;min-height:100%">
    <div style="display:flex;justify-content:space-between;margin-bottom:20px">
      <div>${d.logoHtml}<div style="font-size:18px;font-weight:700;color:white">${business.name||"Your Business"}</div><div style="font-size:11px;color:rgba(255,255,255,0.3);margin-top:6px;line-height:1.8">${d.bizInfo}</div></div>
      <div style="text-align:right"><div style="display:inline-block;background:#c8972a;color:#0f1923;font-size:10px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;padding:4px 12px;border-radius:4px">${type}</div><div style="font-size:24px;font-weight:700;color:white;margin-top:6px">${doc.number||""}</div><div style="font-size:11px;color:rgba(255,255,255,0.3);margin-top:6px;line-height:1.8"><div>Date: ${formatDate(doc.date)}</div>${d.dateLabel}</div></div>
    </div>
    <div style="background:rgba(255,255,255,0.06);border-radius:8px;padding:12px 16px;margin-bottom:16px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:rgba(255,255,255,0.3);margin-bottom:4px">Bill To</div><div style="font-weight:700;font-size:14px;color:white">${doc.client_name||""}</div>${d.paidStamp}</div>
    ${d.tableBody}
    <div style="background:rgba(200,151,42,0.12);border:1px solid rgba(200,151,42,0.25);border-radius:8px;padding:14px 18px;display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
      <div style="font-size:12px;color:rgba(255,255,255,0.4)">Total Due (incl. VAT)</div>
      <div style="font-size:22px;font-weight:700;color:#c8972a">R ${parseFloat(doc.total||0).toFixed(2)}</div>
    </div>
    ${d.notesHtml ? d.notesHtml.replace("background:#fffbeb", "background:rgba(255,255,255,0.06)").replace("color:#92400e", "color:rgba(255,255,255,0.7)") : ""}
    ${d.bankingHtml ? d.bankingHtml.replace("background:#f8fafc", "background:rgba(255,255,255,0.04)").replace(/color:#94a3b8/g, "color:rgba(255,255,255,0.35)").replace(/color:#64748b/g, "color:rgba(255,255,255,0.35)") : ""}
    <div style="margin-top:40px;padding-top:16px;border-top:1px solid rgba(255,255,255,0.08);font-size:11px;color:rgba(255,255,255,0.2);text-align:center">Generated by FieldJob &nbsp;|&nbsp; ${business.name||""} &nbsp;|&nbsp; ${new Date().toLocaleDateString("en-ZA")}</div>
    </div>`;

  // ── Template 6: Blush Pink ───────────────────────────────────────────────
  if (tpl === "6") return `
    <style>
      .thead-row { background:#831843 !important; }
      .thead-row th { color:white; }
      .tbody-rows tr:nth-child(even) td { background:#fdf2f8; }
      .tbody-rows td { border-bottom:1px solid #fce7f3; }
    </style>
    <div style="background:linear-gradient(135deg,#fce7f3,#fdf2f8);margin:-52px -60px 24px;padding:28px 60px;border-bottom:1px solid #fbcfe8;display:flex;justify-content:space-between;align-items:flex-start">
      <div>${d.logoHtml}<div style="font-size:18px;font-weight:700;color:#831843">${business.name||"Your Business"}</div><div style="font-size:11px;color:#be185d;opacity:0.6;margin-top:6px;line-height:1.8">${d.bizInfo}</div></div>
      <div style="text-align:right"><div style="display:inline-block;background:#ec4899;color:white;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;padding:4px 12px;border-radius:20px">${type}</div><div style="font-size:22px;font-weight:700;color:#831843;margin-top:6px">${doc.number||""}</div><div style="font-size:11px;color:#be185d;opacity:0.6;margin-top:6px;line-height:1.8"><div>Date: ${formatDate(doc.date)}</div>${d.dateLabel}</div></div>
    </div>
    <div style="background:#fdf2f8;border-left:3px solid #ec4899;border-radius:0 8px 8px 0;padding:12px 16px;margin-bottom:16px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#be185d;font-weight:700;margin-bottom:4px">Bill To</div><div style="font-weight:700;font-size:14px;color:#831843">${doc.client_name||""}</div>${d.paidStamp}</div>
    ${d.tableBody}
    <div style="text-align:right;padding:14px;background:#fdf2f8;border-radius:8px;margin-bottom:16px;font-size:22px;font-weight:700;color:#831843">Total: R ${parseFloat(doc.total||0).toFixed(2)}</div>
    ${d.notesHtml ? d.notesHtml.replace("background:#fffbeb", "background:#fdf2f8").replace("color:#92400e", "color:#831843").replace("border-left:3px solid #f59e0b", "border-left:3px solid #ec4899") : ""}
    ${d.bankingHtml ? d.bankingHtml.replace("background:#f8fafc", "background:#fdf2f8").replace("border-left:4px solid #c8972a", "border-left:4px solid #ec4899").replace("color:#c8972a", "color:#be185d") : ""}
    <div style="margin-top:40px;padding-top:16px;border-top:1px solid #fbcfe8;font-size:11px;color:#be185d;opacity:0.4;text-align:center">Generated by FieldJob &nbsp;|&nbsp; ${business.name||""} &nbsp;|&nbsp; ${new Date().toLocaleDateString("en-ZA")}</div>`;

  // ── Template 7: Sky Blue ─────────────────────────────────────────────────
  if (tpl === "7") return `
    <style>
      .thead-row { background:#1e3a8a !important; }
      .thead-row th { color:white; }
      .tbody-rows tr:nth-child(even) td { background:#eff6ff; }
      .tbody-rows td { border-bottom:1px solid #dbeafe; }
    </style>
    <div style="background:linear-gradient(135deg,#eff6ff,#dbeafe);margin:-52px -60px 24px;padding:28px 60px;border-bottom:1px solid #bfdbfe;display:flex;justify-content:space-between;align-items:flex-start">
      <div>${d.logoHtml}<div style="font-size:18px;font-weight:700;color:#1e3a8a">${business.name||"Your Business"}</div><div style="font-size:11px;color:#3b82f6;opacity:0.7;margin-top:6px;line-height:1.8">${d.bizInfo}</div></div>
      <div style="text-align:right"><div style="display:inline-block;background:#2563eb;color:white;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;padding:4px 12px;border-radius:20px">${type}</div><div style="font-size:22px;font-weight:700;color:#1e3a8a;margin-top:6px">${doc.number||""}</div><div style="font-size:11px;color:#3b82f6;opacity:0.7;margin-top:6px;line-height:1.8"><div>Date: ${formatDate(doc.date)}</div>${d.dateLabel}</div></div>
    </div>
    <div style="background:#eff6ff;border-left:3px solid #2563eb;border-radius:0 8px 8px 0;padding:12px 16px;margin-bottom:16px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#2563eb;font-weight:700;margin-bottom:4px">Bill To</div><div style="font-weight:700;font-size:14px;color:#1e3a8a">${doc.client_name||""}</div>${d.paidStamp}</div>
    ${d.tableBody}
    <div style="text-align:right;padding:14px;background:#eff6ff;border-radius:8px;margin-bottom:16px;font-size:22px;font-weight:700;color:#1e3a8a">Total: R ${parseFloat(doc.total||0).toFixed(2)}</div>
    ${d.notesHtml ? d.notesHtml.replace("background:#fffbeb", "background:#eff6ff").replace("color:#92400e", "color:#1e3a8a").replace("border-left:3px solid #f59e0b", "border-left:3px solid #2563eb") : ""}
    ${d.bankingHtml ? d.bankingHtml.replace("background:#f8fafc", "background:#eff6ff").replace("border-left:4px solid #c8972a", "border-left:4px solid #2563eb").replace("color:#c8972a", "color:#2563eb") : ""}
    <div style="margin-top:40px;padding-top:16px;border-top:1px solid #bfdbfe;font-size:11px;color:#93c5fd;text-align:center">Generated by FieldJob &nbsp;|&nbsp; ${business.name||""} &nbsp;|&nbsp; ${new Date().toLocaleDateString("en-ZA")}</div>`;

  // ── Template 8: Bubble Orbs ──────────────────────────────────────────────
  if (tpl === "8") return `
    <style>
      .thead-row { background:#0f1923 !important; }
      .thead-row th { color:white; }
      .tbody-rows td { border-bottom:1px solid #f1f5f9; }
    </style>
    <div style="position:relative;background:#0f1923;margin:-52px -60px 24px;padding:28px 60px;overflow:hidden;height:100px;display:flex;align-items:center;justify-content:space-between">
      <div style="position:absolute;top:-30px;right:-30px;width:160px;height:160px;background:#c8972a;opacity:0.15;border-radius:50%"></div>
      <div style="position:absolute;top:10px;right:80px;width:80px;height:80px;background:#c8972a;opacity:0.1;border-radius:50%"></div>
      <div style="position:absolute;bottom:-40px;left:45%;width:140px;height:140px;background:#c8972a;opacity:0.07;border-radius:50%"></div>
      <div style="position:relative;z-index:2">${d.logoHtml}<div style="font-size:18px;font-weight:700;color:white">${business.name||"Your Business"}</div><div style="font-size:11px;color:rgba(255,255,255,0.4);margin-top:4px">${business.phone||""} · ${business.email||""}</div></div>
      <div style="position:relative;z-index:2;text-align:right"><div style="font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#c8972a">${type}</div><div style="font-size:24px;font-weight:700;color:white;margin-top:4px">${doc.number||""}</div></div>
    </div>
    <div style="background:#f8fafc;border-radius:8px;padding:12px 16px;margin-bottom:16px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:4px">Bill To</div><div style="font-weight:700;font-size:14px">${doc.client_name||""}</div><div style="font-size:12px;color:#64748b;margin-top:3px">Date: ${formatDate(doc.date)} &nbsp;·&nbsp; ${d.dateLabel.replace(/<[^>]*>/g,"")}</div>${d.paidStamp}</div>
    ${d.tableBody}
    <div style="text-align:right;padding:16px;background:#f8fafc;border-radius:8px;margin-bottom:16px">${d.totalsHtml}</div>
    ${d.notesHtml}${d.bankingHtml}${d.payLinks||""}${footer}`;

  // ── Template 9: Diagonal Slash ───────────────────────────────────────────
  if (tpl === "9") return `
    <style>
      .thead-row { background:#0f1923 !important; }
      .thead-row th { color:white; }
      .tbody-rows td { border-bottom:1px solid #f1f5f9; }
    </style>
    <div style="position:relative;margin:-52px -60px 24px;height:100px;overflow:hidden">
      <div style="position:absolute;inset:0;background:#0f1923"></div>
      <div style="position:absolute;bottom:0;right:0;width:55%;height:100%;background:white;transform:skewX(-10deg) translateX(20px);transform-origin:bottom right"></div>
      <div style="position:absolute;bottom:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#c8972a,#f0d078 40%,transparent)"></div>
      <div style="position:relative;z-index:2;padding:20px 60px;display:flex;justify-content:space-between;align-items:center;height:100%">
        <div>${d.logoHtml}<div style="font-size:17px;font-weight:700;color:white">${business.name||"Your Business"}</div><div style="font-size:11px;color:rgba(255,255,255,0.4);margin-top:3px">${business.phone||""} · ${business.email||""}</div></div>
        <div style="text-align:right"><div style="font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#c8972a">${type}</div><div style="font-size:22px;font-weight:700;color:#0f1923;margin-top:4px">${doc.number||""}</div></div>
      </div>
    </div>
    <div style="background:#f8fafc;border-radius:8px;padding:12px 16px;margin-bottom:16px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:4px">Bill To</div><div style="font-weight:700;font-size:14px">${doc.client_name||""}</div><div style="font-size:12px;color:#64748b;margin-top:3px">Date: ${formatDate(doc.date)}</div>${d.paidStamp}</div>
    ${d.tableBody}
    <div style="text-align:right;padding:16px;background:#f8fafc;border-radius:8px;margin-bottom:16px">${d.totalsHtml}</div>
    ${d.notesHtml}${d.bankingHtml}${d.payLinks||""}${footer}`;

  // ── Template 10: Wave ────────────────────────────────────────────────────
  if (tpl === "10") return `
    <style>
      .thead-row { background:#0f1923 !important; }
      .thead-row th { color:white; }
      .tbody-rows td { border-bottom:1px solid #f1f5f9; }
    </style>
    <div style="position:relative;background:#0f1923;margin:-52px -60px 24px;padding:20px 60px 0">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px">
        <div>${d.logoHtml}<div style="font-size:17px;font-weight:700;color:white">${business.name||"Your Business"}</div><div style="font-size:11px;color:rgba(255,255,255,0.4);margin-top:3px">${business.phone||""} · ${business.email||""}</div></div>
        <div style="text-align:right"><div style="font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#c8972a">${type}</div><div style="font-size:22px;font-weight:700;color:white;margin-top:4px">${doc.number||""}</div><div style="font-size:11px;color:rgba(255,255,255,0.4);margin-top:3px">Date: ${formatDate(doc.date)}</div></div>
      </div>
      <svg viewBox="0 0 794 32" preserveAspectRatio="none" style="display:block;width:calc(100% + 120px);margin-left:-60px;height:32px"><path d="M0,16 C150,32 300,0 450,16 C600,32 700,8 794,16 L794,32 L0,32Z" fill="white"/></svg>
    </div>
    <div style="background:#f8fafc;border-radius:8px;padding:12px 16px;margin-bottom:16px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:4px">Bill To</div><div style="font-weight:700;font-size:14px">${doc.client_name||""}</div><div style="font-size:12px;color:#64748b;margin-top:3px">${d.dateLabel.replace(/<[^>]*>/g,"")}</div>${d.paidStamp}</div>
    ${d.tableBody}
    <div style="text-align:right;padding:16px;background:#f8fafc;border-radius:8px;margin-bottom:16px">${d.totalsHtml}</div>
    ${d.notesHtml}${d.bankingHtml}${d.payLinks||""}${footer}`;

  // ── Template 11: Triangle Corner ────────────────────────────────────────
  if (tpl === "11") return `
    <style>
      .thead-row { background:#0f1923 !important; }
      .thead-row th { color:white; }
      .tbody-rows td { border-bottom:1px solid #f1f5f9; }
    </style>
    <div style="position:relative;margin:-52px -60px 24px;height:100px;overflow:hidden;background:white;border-bottom:1px solid #e2e8f0">
      <div style="position:absolute;top:0;right:0;width:0;height:0;border-style:solid;border-width:0 180px 100px 0;border-color:transparent #0f1923 transparent transparent"></div>
      <div style="position:absolute;top:0;right:0;width:0;height:0;border-style:solid;border-width:0 145px 80px 0;border-color:transparent #c8972a transparent transparent;opacity:0.3"></div>
      <div style="position:relative;z-index:2;padding:20px 60px;display:flex;justify-content:space-between;align-items:center;height:100%">
        <div>${d.logoHtml}<div style="font-size:17px;font-weight:700;color:#0f1923">${business.name||"Your Business"}</div><div style="font-size:11px;color:#94a3b8;margin-top:3px">${business.phone||""} · ${business.email||""}</div></div>
        <div style="text-align:right"><div style="font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:white">${type}</div><div style="font-size:22px;font-weight:700;color:white;margin-top:4px">${doc.number||""}</div></div>
      </div>
    </div>
    <div style="background:#f8fafc;border-radius:8px;padding:12px 16px;margin-bottom:16px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:4px">Bill To</div><div style="font-weight:700;font-size:14px">${doc.client_name||""}</div><div style="font-size:12px;color:#64748b;margin-top:3px">Date: ${formatDate(doc.date)}</div>${d.paidStamp}</div>
    ${d.tableBody}
    <div style="text-align:right;padding:16px;background:#f8fafc;border-radius:8px;margin-bottom:16px">${d.totalsHtml}</div>
    ${d.notesHtml}${d.bankingHtml}${d.payLinks||""}${footer}`;

  // ── Template 12: Split Column ────────────────────────────────────────────
  if (tpl === "12") return `
    <style>
      .thead-row { background:#0f1923 !important; }
      .thead-row th { color:white; }
      .tbody-rows td { border-bottom:1px solid #f1f5f9; }
    </style>
    <div style="margin:-52px -60px 24px;display:flex;height:100px">
      <div style="background:#0f1923;width:40%;padding:20px 28px;display:flex;flex-direction:column;justify-content:center">${d.logoHtml}<div style="font-size:15px;font-weight:700;color:white">${business.name||"Your Business"}</div><div style="font-size:10px;color:rgba(255,255,255,0.3);margin-top:3px">${business.phone||""} · ${business.email||""}</div></div>
      <div style="background:white;flex:1;padding:20px 28px;display:flex;flex-direction:column;justify-content:center;align-items:flex-end;border-bottom:3px solid #c8972a"><div style="font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#94a3b8">${type}</div><div style="font-size:24px;font-weight:700;color:#0f1923;margin-top:4px">${doc.number||""}</div><div style="font-size:11px;color:#64748b;margin-top:3px">Date: ${formatDate(doc.date)}</div></div>
    </div>
    <div style="background:#f8fafc;border-radius:8px;padding:12px 16px;margin-bottom:16px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:4px">Bill To</div><div style="font-weight:700;font-size:14px">${doc.client_name||""}</div>${d.paidStamp}</div>
    ${d.tableBody}
    <div style="text-align:right;padding:16px;background:#f8fafc;border-radius:8px;margin-bottom:16px">${d.totalsHtml}</div>
    ${d.notesHtml}${d.bankingHtml}${d.payLinks||""}${footer}`;

  // fallback to classic
  return buildDocHTML({...doc}, type, {...business, pdf_template:"1"});
}

function buildReceiptHTML(doc, business) {
  const tpl = business.pdf_template || "1";
  const logoHtml = business.logo ? `<img src="${business.logo}" style="max-width:100px;max-height:60px;object-fit:contain;margin-bottom:8px;display:block;margin-left:auto;margin-right:auto;" alt="logo"loading="lazy" />` : "";
  const accentColor = tpl==="6" ? "#ec4899" : tpl==="7" ? "#2563eb" : "#c8972a";
  const accentDark  = tpl==="6" ? "#831843" : tpl==="7" ? "#1e3a8a" : "#0f1923";
  const bgLight     = tpl==="6" ? "#fdf2f8" : tpl==="7" ? "#eff6ff" : "#f8fafc";
  const borderLight = tpl==="6" ? "#fbcfe8" : tpl==="7" ? "#bfdbfe" : "#e2e8f0";
  const amountBg    = tpl==="5" ? "rgba(200,151,42,0.12)" : tpl==="6" ? "#fdf2f8" : tpl==="7" ? "#eff6ff" : "#f0fdf4";
  const amountColor = tpl==="5" ? "#c8972a" : tpl==="6" ? "#be185d" : tpl==="7" ? "#1e3a8a" : "#10b981";
  return `
    <div style="text-align:center;border-bottom:2px solid ${borderLight};padding-bottom:24px;margin-bottom:28px">
      ${logoHtml}
      <h2 style="font-size:20px;font-weight:700;color:${accentDark}">${business.name||"Business Name"}</h2>
      ${business.address ? `<div style="font-size:12px;color:#64748b;margin-top:4px">${business.address}</div>` : ""}
      ${business.phone ? `<div style="font-size:12px;color:#64748b">Tel: ${business.phone}</div>` : ""}
      <div style="font-size:24px;font-weight:700;color:${accentColor};margin-top:16px;letter-spacing:1px">PAYMENT RECEIPT</div>
      <div style="color:#64748b;margin-top:4px;font-size:14px">${doc.number||""}</div>
    </div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:28px">
      <tr><td style="padding:12px 0;border-bottom:1px solid ${borderLight};color:#64748b">Received From</td><td style="padding:12px 0;border-bottom:1px solid ${borderLight};font-weight:700;text-align:right">${doc.client_name||""}</td></tr>
      <tr><td style="padding:12px 0;border-bottom:1px solid ${borderLight};color:#64748b">Invoice Reference</td><td style="padding:12px 0;border-bottom:1px solid ${borderLight};font-weight:700;text-align:right">${doc.invoice_number||""}</td></tr>
      <tr><td style="padding:12px 0;border-bottom:1px solid ${borderLight};color:#64748b">Payment Date</td><td style="padding:12px 0;border-bottom:1px solid ${borderLight};font-weight:700;text-align:right">${formatDate(doc.date)}</td></tr>
      <tr><td style="padding:12px 0;border-bottom:1px solid ${borderLight};color:#64748b">Payment Method</td><td style="padding:12px 0;border-bottom:1px solid ${borderLight};text-align:right"><strong>${doc.payment_method||""}</strong></td></tr>
    </table>
    <div style="background:${amountBg};padding:28px;border-radius:12px;text-align:center;margin-bottom:24px;border:2px solid ${borderLight}">
      <div style="color:#64748b;font-size:13px;margin-bottom:6px">Amount Received</div>
      <div style="font-size:42px;font-weight:700;color:${amountColor}">R ${parseFloat(doc.amount||0).toFixed(2)}</div>
    </div>
    <div style="text-align:center;font-size:13px;color:#64748b;margin-bottom:8px">Thank you for your payment. This is your official receipt.</div>
    <div style="margin-top:32px;padding-top:16px;border-top:2px solid ${borderLight};font-size:11px;color:#94a3b8;text-align:center">Generated by FieldJob &nbsp;|&nbsp; ${business.name||""} &nbsp;|&nbsp; ${new Date().toLocaleDateString("en-ZA")}</div>`;
}

function buildEstimateDocHTML(est, business) {
  const tpl = business?.pdf_template || "1";
  const accentColor = tpl==="6" ? "#ec4899" : tpl==="5" ? "#c8972a" : tpl==="3" ? "#c8972a" : "#c8972a";
  const headerBg    = tpl==="5" ? "#0f1923" : tpl==="6" ? "linear-gradient(135deg,#fce7f3,#fdf2f8)" : tpl==="2" ? "#0f1923" : tpl==="3" ? "linear-gradient(90deg,#c8972a,#e4ae3a,#c8972a)" : "#0f1923";
  const headerTxt   = tpl==="6" ? "#831843" : "white";
  const sections = typeof est.sections === "string" ? JSON.parse(est.sections||"[]") : (est.sections||[]);
  const validUntil = est.valid_until ? formatDate(est.valid_until) : "";

  const sectionRows = sections.map((sec, idx) => {
    const matRows = (sec.matLines||[]).filter(l => l.desc||l.cost).map(l => {
      const tot = (parseFloat(l.qty)||0)*(parseFloat(l.cost)||0);
      return `<tr><td style="padding:4px 8px;font-size:10px">${l.desc||""}</td><td style="padding:4px 8px;font-size:10px;text-align:right">${l.qty||""}</td><td style="padding:4px 8px;font-size:10px;text-align:right">${l.unit||""}</td><td style="padding:4px 8px;font-size:10px;text-align:right">${l.cost?formatZAR(parseFloat(l.cost)):""}</td><td style="padding:4px 8px;font-size:10px;text-align:right;font-weight:700">${tot>0?formatZAR(tot):""}</td></tr>`;
    }).join("");
    const labRows = (sec.labLines||[]).filter(l => l.desc||l.rate).map(l => {
      const tot = (parseFloat(l.hrs)||0)*(parseFloat(l.rate)||0);
      return `<tr><td style="padding:4px 8px;font-size:10px">${l.desc||""}</td><td style="padding:4px 8px;font-size:10px;text-align:right">${l.hrs||""}</td><td style="padding:4px 8px;font-size:10px;text-align:right">${l.unit||""}</td><td style="padding:4px 8px;font-size:10px;text-align:right">${l.rate?formatZAR(parseFloat(l.rate)):""}</td><td style="padding:4px 8px;font-size:10px;text-align:right;font-weight:700">${tot>0?formatZAR(tot):""}</td></tr>`;
    }).join("");
    const matBlock = matRows ? `<div style="background:rgba(59,130,246,0.06);padding:5px 10px;font-size:9px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#3b82f6;border-bottom:1px solid rgba(59,130,246,0.12)">Materials</div><table style="width:100%;border-collapse:collapse"><thead><tr>${["Description","Qty","Unit","Cost","Total"].map(h=>`<th style="font-size:8px;font-weight:700;text-transform:uppercase;color:#94a3b8;padding:4px 8px;border-bottom:1px solid #e4e9f0;text-align:${h==="Description"?"left":"right"}">${h}</th>`).join("")}</tr></thead><tbody>${matRows}</tbody></table>` : "";
    const labBlock = labRows ? `<div style="background:rgba(200,151,42,0.06);padding:5px 10px;font-size:9px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#c8972a;border-bottom:1px solid rgba(200,151,42,0.15);border-top:1px solid #e4e9f0">Labour</div><table style="width:100%;border-collapse:collapse"><thead><tr>${["Description","Hrs/Days","Unit","Rate","Total"].map(h=>`<th style="font-size:8px;font-weight:700;text-transform:uppercase;color:#94a3b8;padding:4px 8px;border-bottom:1px solid #e4e9f0;text-align:${h==="Description"?"left":"right"}">${h}</th>`).join("")}</tr></thead><tbody>${labRows}</tbody></table>` : "";
    const st_mat = (sec.matLines||[]).reduce((a,l)=>a+(parseFloat(l.qty)||0)*(parseFloat(l.cost)||0),0);
    const st_lab = (sec.labLines||[]).reduce((a,l)=>a+(parseFloat(l.hrs)||0)*(parseFloat(l.rate)||0),0);
    const scopeBlock = (sec.inclusions||sec.exclusions) ? `<div style="padding:5px 10px;background:#f8f9fb;font-size:9px;line-height:1.6">${sec.inclusions?`<div><strong>✓ Inclusions:</strong> ${sec.inclusions}</div>`:""}${sec.exclusions?`<div><strong style="color:#ef4444">✕ Exclusions:</strong> ${sec.exclusions}</div>`:""}</div>` : "";
    return `<div style="margin-bottom:14px"><div style="background:#0f1923;border-radius:6px 6px 0 0;padding:7px 11px;display:flex;align-items:center;justify-content:space-between"><span style="font-size:8.5px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#c8972a">Section ${idx+1}</span><span style="font-size:11px;font-weight:700;color:white">${sec.name||"Untitled Section"}</span></div>${matBlock}${labBlock}${scopeBlock}<div style="display:flex;justify-content:space-between;align-items:center;padding:7px 10px;background:#f1f4f8;border-radius:0 0 6px 6px"><div style="display:flex;gap:14px"><span style="font-size:9px;color:#64748b">Materials <strong style="color:#1a2535">${formatZAR(st_mat)}</strong></span><span style="font-size:9px;color:#64748b">Labour <strong style="color:#1a2535">${formatZAR(st_lab)}</strong></span></div><span style="font-size:13px;font-weight:700;color:#0f1923">${formatZAR(st_mat+st_lab)}</span></div></div>`;
  }).join("");

  const contPct = parseFloat(est.contingency_pct)||0;
  const vatMode = est.vat_mode||"15";
  const totalsRows = [
    ["Total Materials", formatCurrency(est.total_materials||0, business.currency)],
    ["Total Labour",    formatCurrency(est.total_labour||0, business.currency)],
    ["Subtotal (excl. tax)", formatCurrency(est.subtotal||0, business.currency)],
    ...(contPct>0 ? [[`Contingency (${contPct}%)`, formatCurrency(est.contingency_amt||0, business.currency)]] : []),
    [`${business.tax_label||"VAT"} (${vatMode==="15"?`${business.tax_rate||15}%`:vatMode==="excl"?"Excluded":"N/A"})`, vatMode==="na"?"N/A":formatCurrency(est.vat_amt||0, business.currency)],
  ].map(([l,v]) => `<div style="display:flex;justify-content:space-between;padding:4px 0;font-size:10.5px;border-bottom:1px solid #f1f4f8"><span style="color:#64748b">${l}</span><span style="font-weight:700">${v}</span></div>`).join("");

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:'IBM Plex Sans',Arial,sans-serif;background:#d1d5db;padding:20px;color:#1a2535;}
    .page{background:white;max-width:680px;margin:0 auto;border-radius:4px;overflow:hidden;}
  </style></head><body><div class="page">
    <div style="background:#0f1923;padding:22px 24px 18px;position:relative">
      <div style="position:absolute;bottom:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,#c8972a,transparent)"></div>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px">
        <div style="display:flex;align-items:center;gap:8px">
          <div style="width:28px;height:28px;border-radius:7px;background:rgba(200,151,42,0.15);border:1px solid rgba(200,151,42,0.28);display:flex;align-items:center;justify-content:center">
            <span style="font-size:14px;font-weight:700;color:#c8972a">FJ</span>
          </div>
          <span style="font-size:15px;font-weight:700;color:white">${business.name||"FieldJob"}</span>
        </div>
        <div style="text-align:right">
          <div style="font-size:22px;font-weight:700;color:#c8972a">ESTIMATE</div>
          <div style="font-size:10px;color:rgba(255,255,255,0.3);margin-top:2px">${est.number||""}</div>
          <div style="font-size:7.5px;color:rgba(200,151,42,0.45);letter-spacing:1px;text-transform:uppercase;margin-top:2px">Not a Tax Invoice</div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px">
        ${[
          ["Prepared For", [est.client_name,est.client_phone].filter(Boolean).join(" · ")||"—"],
          ["Site Address",  est.site_address||"—"],
          ["Date",          formatDate(est.date)],
          ["Valid Until",   validUntil||"—"],
          ["Prepared By",   est.prepared_by||"—"],
          ["Estimate No.",  est.number||""],
        ].map(([l,v])=>`<div><div style="font-size:8px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:rgba(255,255,255,0.25);margin-bottom:2px">${l}</div><div style="font-size:10.5px;color:rgba(255,255,255,0.8);font-weight:500">${v}</div></div>`).join("")}
      </div>
    </div>
    <div style="padding:16px 20px">
      ${est.job_description?`<div style="font-size:10.5px;color:#64748b;background:#f8f9fb;border-radius:6px;padding:8px 10px;margin-bottom:14px;line-height:1.6">${est.job_description}</div>`:""}
      ${sectionRows}
      <div style="border-top:1px solid #e4e9f0;margin-top:12px;padding-top:10px">${totalsRows}</div>
      <div style="display:flex;justify-content:space-between;align-items:center;background:#0f1923;border-radius:8px;padding:12px 14px;margin-top:10px">
        <span style="font-size:11px;font-weight:700;color:rgba(255,255,255,0.5)">GRAND TOTAL ESTIMATE</span>
        <span style="font-size:22px;font-weight:700;color:#c8972a">${formatZAR(est.grand_total||0)}</span>
      </div>
      ${(est.assumptions||est.payment_terms)?`<div style="margin-top:14px;padding-top:10px;border-top:1px solid #e4e9f0">
        <div style="font-size:8.5px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#94a3b8;margin-bottom:8px">Notes & Terms</div>
        ${est.assumptions?`<div style="margin-bottom:7px"><div style="font-size:9px;font-weight:700;color:#0f1923;margin-bottom:2px">Basis of Estimate</div><div style="font-size:9.5px;color:#64748b;line-height:1.6">${est.assumptions}</div></div>`:""}
        ${est.payment_terms?`<div><div style="font-size:9px;font-weight:700;color:#0f1923;margin-bottom:2px">Payment Terms</div><div style="font-size:9.5px;color:#64748b;line-height:1.6">${est.payment_terms}</div></div>`:""}
      </div>`:""}
    </div>
    <div style="background:#f8f9fb;border-top:1px solid #e4e9f0;padding:10px 20px;display:flex;justify-content:space-between;align-items:center">
      <span style="font-size:8.5px;color:#94a3b8">${business.name||"FieldJob"} · ${business.email||""} · ${business.phone||""}</span>
      <span style="font-size:8.5px;color:#94a3b8">Valid until <strong style="color:#0f1923">${validUntil}</strong></span>
    </div>
  </div></body></html>`;
}

function buildJobReportHTML(job, business, expenses) {
  const tpl = business.pdf_template || "1";
  const accentColor = tpl==="6" ? "#ec4899" : tpl==="7" ? "#2563eb" : "#c8972a";
  const accentDark  = tpl==="6" ? "#831843" : tpl==="7" ? "#1e3a8a" : "#0f1923";
  const bgLight     = tpl==="6" ? "#fdf2f8" : tpl==="7" ? "#eff6ff" : "#f8fafc";
  const jobExp = (expenses||[]).filter(e=>e.job_id===job.id);
  const totalExp = jobExp.reduce((s,e)=>s+(parseFloat(e.amount)||0),0);
  const profit = (job.estimated_cost||0) - totalExp;
  const logoHtml = business.logo ? `<img src="${business.logo}" style="max-width:110px;max-height:65px;object-fit:contain;margin-bottom:8px;display:block;" alt="logo"loading="lazy" />` : "";
  const hdrStyle = tpl==="5"
    ? `background:#0f1923;color:white;margin:-52px -60px 24px;padding:28px 60px;display:flex;justify-content:space-between;align-items:flex-start`
    : tpl==="6" ? `background:linear-gradient(135deg,#fce7f3,#fdf2f8);border-bottom:1px solid #fbcfe8;margin:-52px -60px 24px;padding:28px 60px;display:flex;justify-content:space-between;align-items:flex-start`
    : tpl==="7" ? `background:linear-gradient(135deg,#eff6ff,#dbeafe);border-bottom:1px solid #bfdbfe;margin:-52px -60px 24px;padding:28px 60px;display:flex;justify-content:space-between;align-items:flex-start`
    : `display:flex;justify-content:space-between;margin-bottom:20px;padding-bottom:16px;border-bottom:2px solid #e2e8f0`;
  const bizNameColor = tpl==="5" ? "white" : accentDark;
  const bizInfoColor = tpl==="5" ? "rgba(255,255,255,0.4)" : "#64748b";
  return `
    <div style="${hdrStyle}">
      <div>${logoHtml}<h2 style="font-size:18px;font-weight:700;color:${bizNameColor}">${business.name||"Your Business"}</h2><div style="color:${bizInfoColor};font-size:12px;margin-top:6px;line-height:1.8">${business.phone ? `<div>Tel: ${business.phone}</div>` : ""}${business.email ? `<div>${business.email}</div>` : ""}</div></div>
      <div style="text-align:right"><div style="font-size:11px;color:${accentColor};text-transform:uppercase;letter-spacing:1px;font-weight:700">Job Report</div><div style="font-size:20px;font-weight:700;color:${accentColor};margin-top:4px">${job.title||""}</div><div style="font-size:12px;color:${bizInfoColor};margin-top:4px">${formatDate(job.date)}</div></div>
    </div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:24px">
      <tr><td style="padding:10px 0;border-bottom:1px solid #e2e8f0;color:#64748b;width:35%">Client</td><td style="padding:10px 0;border-bottom:1px solid #e2e8f0;font-weight:700">${job.client_name||"—"}</td></tr>
      <tr><td style="padding:10px 0;border-bottom:1px solid #e2e8f0;color:#64748b">Technician(s)</td><td style="padding:10px 0;border-bottom:1px solid #e2e8f0">${(job.worker_names||[]).length > 0 ? job.worker_names.join(", ") : (job.worker_name||"—")}</td></tr>
      <tr><td style="padding:10px 0;border-bottom:1px solid #e2e8f0;color:#64748b">Address</td><td style="padding:10px 0;border-bottom:1px solid #e2e8f0">${job.address||"—"}</td></tr>
      <tr><td style="padding:10px 0;border-bottom:1px solid #e2e8f0;color:#64748b">Status</td><td style="padding:10px 0;border-bottom:1px solid #e2e8f0"><strong>${job.status||""}</strong></td></tr>
    </table>
    ${job.description ? `<div style="margin-bottom:16px"><div style="font-weight:700;margin-bottom:6px">Description of Work</div><div style="color:#64748b;line-height:1.7">${job.description}</div></div>` : ""}
    ${job.materials ? `<div style="margin-bottom:16px"><div style="font-weight:700;margin-bottom:6px">Materials Used</div><div style="color:#64748b;line-height:1.7">${job.materials}</div></div>` : ""}
    ${jobExp.length>0 ? `<div style="margin-bottom:16px"><div style="font-weight:700;margin-bottom:10px">Expenses</div><table style="width:100%;border-collapse:collapse"><thead><tr style="background:${accentDark};color:white"><th style="padding:8px 12px;text-align:left">Category</th><th style="padding:8px 12px;text-align:left">Description</th><th style="padding:8px 12px;text-align:right">Amount</th></tr></thead><tbody>${jobExp.map(e=>`<tr><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0">${e.category}</td><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0">${e.description||"—"}</td><td style="padding:8px 12px;border-bottom:1px solid #e2e8f0;text-align:right">R ${parseFloat(e.amount||0).toFixed(2)}</td></tr>`).join("")}</tbody></table></div>` : ""}
    <div style="background:${bgLight};padding:16px;border-radius:8px;margin-bottom:16px;border-left:4px solid ${accentColor}">
      <div style="display:flex;justify-content:space-between;margin-bottom:6px"><span style="color:#64748b">Job Value</span><span style="font-weight:700">R ${parseFloat(job.estimated_cost||0).toFixed(2)}</span></div>
      ${jobExp.length>0 ? `<div style="display:flex;justify-content:space-between;margin-bottom:6px"><span style="color:#64748b">Total Expenses</span><span style="color:#ef4444;font-weight:600">- R ${totalExp.toFixed(2)}</span></div><div style="display:flex;justify-content:space-between;font-size:16px;font-weight:700;padding-top:10px;border-top:1px solid #e2e8f0"><span>Profit</span><span style="color:${profit>=0?"#10b981":"#ef4444"}">R ${profit.toFixed(2)}</span></div>` : ""}
    </div>
    ${job.notes ? `<div style="padding:12px;background:#fffbeb;border-radius:8px;font-size:12.5px;color:#92400e;border-left:3px solid #f59e0b"><strong>Internal Notes:</strong> ${job.notes}</div>` : ""}
    ${[["photos_before","📷 Before Photos"],["photos_during","🔧 During Photos"],["photos_after","✅ After Photos"]].map(([field,label]) => {
      const photos = job[field];
      if (!photos || photos.length === 0) return "";
      return `<div style="margin-top:20px"><div style="font-weight:700;margin-bottom:10px;font-size:14px">${label}</div><div style="display:flex;gap:10px;flex-wrap:wrap">${photos.map(src => `<img src="${src}" style="width:160px;height:120px;object-fit:cover;border-radius:8px;border:1px solid #e2e8f0;" loading="lazy" />`).join("")}</div></div>`;
    }).join("")}
    <div style="margin-top:32px;padding-top:16px;border-top:2px solid #e2e8f0;font-size:11px;color:#94a3b8;text-align:center">Generated by FieldJob &nbsp;|&nbsp; ${business.name||""} &nbsp;|&nbsp; ${new Date().toLocaleDateString("en-ZA")}</div>`;
}

// ─── EMAIL HELPERS ────────────────────────────────────────────────────────────
// Builds a mailto: link with pre-filled subject and body
// ─── EXPORT EXPENSES TO XLSX ─────────────────────────────────────────────────
function exportExpensesXLSX(expenses, tabLabel) {
  // Dynamically load SheetJS from CDN
  const loadAndExport = () => {
    const aoa = [
      ["Date","Category","Description","Job","Supplier","Amount (R)","Deductible %","VAT Claimable","Receipt Ref","Notes"],
    ];
    expenses.forEach(e => {
      aoa.push([
        e.date||"", e.category||"", e.description||"", e.job_id||"",
        e.supplier||"", parseFloat(e.amount||0).toFixed(2),
        e.deductible_pct != null ? e.deductible_pct : 100,
        e.vat_claimable ? "Yes" : "No",
        e.receipt_ref||"", e.notes||"",
      ]);
    });
    const ws = window.XLSX.utils.aoa_to_sheet(aoa);
    // Column widths
    ws["!cols"] = [10,24,30,16,20,12,12,12,14,30].map(w=>({wch:w}));
    // Bold header row
    const range = window.XLSX.utils.decode_range(ws["!ref"]);
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = ws[window.XLSX.utils.encode_cell({r:0,c})];
      if (cell) { cell.s = { font:{ bold:true } }; }
    }
    const wb = window.XLSX.utils.book_new();
    window.XLSX.utils.book_append_sheet(wb, ws, "Expenses");
    const label = tabLabel === "all" ? "All" : tabLabel.replace(/[^a-zA-Z0-9]/g,"_");
    window.XLSX.writeFile(wb, `FieldJob_Expenses_${label}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };
  if (window.XLSX) { loadAndExport(); return; }
  const script = document.createElement("script");
  script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
  script.onload = loadAndExport;
  document.head.appendChild(script);
}

// ─── BUILD STATUS REPORT HTML (Landscape PDF) ────────────────────────────────
function buildStatusReportHTML(report, business) {
  const type = (report.report_type||"weekly").toLowerCase();
  const isDaily = type === "daily";
  const isMonthly = type === "monthly";
  const showBudget = !isDaily;
  const showMilestones = !isDaily;

  const biz = business || {};
  const accentGold = "#c8972a";
  const ink = "#0f1923";

  const safe = (v) => (v||"").toString().replace(/</g,"&lt;").replace(/>/g,"&gt;");
  const parseJ = (v) => { try { return typeof v==="string" ? JSON.parse(v||"[]") : (v||[]); } catch { return []; } };
  const fmtR = (v) => v != null && v !== "" ? `R ${parseFloat(v).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2})}` : "—";

  const workItems    = parseJ(report.work_items);
  const crewItems    = parseJ(report.crew_items);
  const eqpItems     = parseJ(report.equipment_items);
  const matItems     = parseJ(report.material_items);
  const issues       = parseJ(report.issues);
  const milestones   = parseJ(report.milestones);
  const nextPlan     = parseJ(report.next_plan);
  const photos       = parseJ(report.photos);
  const visitors     = parseJ(report.visitors);
  const upcomingMS   = parseJ(report.upcoming_milestones);

  const statusColors = { "On Track":"#10b981","At Risk":"#f59e0b","Delayed":"#ef4444","Completed":"#0ea5e9" };
  const sColor = statusColors[report.overall_status] || accentGold;

  const periodLabel = report.date_from && report.date_to
    ? `${report.date_from} – ${report.date_to}`
    : report.date_from || report.date_to || "";

  // ── WORK ITEMS TABLE
  const workRows = workItems.length ? workItems.map(w => {
    const pct = parseFloat(w.pct||0);
    const sc = w.status==="Done"?"#10b981":w.status==="In Progress"?"#f59e0b":"#94a3b8";
    return `<tr style="border-bottom:1px solid #f1f5f9">
      <td style="padding:4px 8px;font-size:9px;font-weight:600">${safe(w.task)}</td>
      <td style="padding:4px 8px;font-size:9px;color:#64748b">${safe(w.area)}</td>
      <td style="padding:4px 6px"><span style="font-size:8px;font-weight:700;padding:2px 7px;border-radius:100px;background:${sc}18;color:${sc}">${safe(w.status)}</span></td>
      <td style="padding:4px 8px;font-size:9px;font-weight:700">${pct}%</td>
      <td style="padding:4px 8px;font-size:9px;color:#64748b">${safe(w.notes)}</td>
    </tr>`;
  }).join("") : `<tr><td colspan="5" style="padding:8px;font-size:9px;color:#94a3b8;text-align:center">No activities recorded</td></tr>`;

  // ── ISSUES
  const issueRows = issues.length ? issues.map(i => {
    const ic = i.impact==="High"?"#ef4444":i.impact==="Medium"?"#f59e0b":"#10b981";
    const sc = i.status==="Open"?"#f59e0b":"#10b981";
    return `<div style="padding:6px 10px;background:${ic}08;border-left:3px solid ${ic};border-radius:0 4px 4px 0;margin-bottom:5px">
      <div style="font-size:9.5px;font-weight:600">${safe(i.description)}</div>
      <div style="font-size:8.5px;color:#64748b;margin-top:2px">Impact: <strong>${safe(i.impact)}</strong> · Cause: ${safe(i.cause)} · <span style="color:${sc}">${safe(i.status)}</span></div>
      ${i.resolution?`<div style="font-size:8.5px;color:#475569;margin-top:2px">→ ${safe(i.resolution)}</div>`:""}
    </div>`;
  }).join("") : `<div style="font-size:9px;color:#94a3b8;padding:6px">No issues recorded</div>`;

  // ── NEXT PLAN
  const planRows = nextPlan.length ? nextPlan.map(p =>
    `<div style="padding:5px 10px;background:#eff6ff;border-radius:4px;margin-bottom:4px;font-size:9px;display:flex;gap:8px;align-items:flex-start">
      <span style="color:#0ea5e9;font-weight:700;flex-shrink:0">→</span>
      <span style="flex:1">${safe(p.task)}</span>
      <span style="color:#94a3b8;flex-shrink:0">${safe(p.responsible)} · ${safe(p.target_date)}</span>
    </div>`
  ).join("") : `<div style="font-size:9px;color:#94a3b8;padding:6px">No plan recorded</div>`;

  // ── PHOTOS (max 4 shown in PDF — 2 per row)
  const photoGrid = photos.slice(0,4).length ? `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:6px">
      ${photos.slice(0,4).map(p => {
        const catC = p.category==="Issue"?"#ef4444":p.category==="Safety"?"#10b981":p.category==="Completed"?"#0ea5e9":"#c8972a";
        return `<div style="border:1px solid #e2e8f0;border-radius:5px;overflow:hidden">
          <div style="height:60px;background:linear-gradient(135deg,#f1f5f9,#e2e8f0);display:flex;align-items:center;justify-content:center;position:relative">
            ${p.url ? `<img src="${p.url}" style="width:100%;height:60px;object-fit:cover" onerror="this.style.display='none'"loading="lazy" />` : `<span style="font-size:20px">🖼</span>`}
            <span style="position:absolute;top:3px;right:3px;font-size:7px;font-weight:700;padding:1px 5px;border-radius:100px;background:${catC}20;color:${catC}">${safe(p.category)}</span>
          </div>
          <div style="padding:3px 6px;font-size:7.5px;color:#64748b;background:#fafbfc">${safe(p.caption||"")}</div>
        </div>`;
      }).join("")}
    </div>
    ${photos.length>4?`<div style="font-size:8px;color:#94a3b8;margin-top:4px">+ ${photos.length-4} more photo(s) — see Excel export</div>`:""}
  ` : `<div style="font-size:9px;color:#94a3b8">No photos attached</div>`;

  // ── CREW SUMMARY
  const crewSummary = crewItems.length
    ? `${crewItems.length} worker${crewItems.length>1?"s":""} on site · ${crewItems.reduce((a,c)=>a+(parseFloat(c.hours)||0),0).toFixed(1)} hrs total`
    : "No crew recorded";

  // ── SAFETY SUMMARY
  const safetySummary = [
    `Briefing: <strong style="color:${report.safety_briefing?"#10b981":"#ef4444"}">${report.safety_briefing?"Yes ✓":"No ✗"}</strong>`,
    `Incidents: <strong>${report.incidents?"Yes — see notes":"None"}</strong>`,
    `PPE: <strong>${safe(report.ppe_compliance||"—")}</strong>`,
  ].join("  ·  ");

  // ── MILESTONES
  const msRows = milestones.length ? milestones.map(m=>
    `<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;padding:4px 8px;background:#10b98108;border-radius:4px;font-size:8.5px">
      <span style="color:#10b981;font-weight:700">✓</span><span>${safe(m.name)}</span><span style="color:#94a3b8;margin-left:auto">${safe(m.date)}</span>
    </div>`
  ).join("") : "";
  const upRows = upcomingMS.length ? upcomingMS.map(m=>
    `<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;padding:4px 8px;background:#0ea5e908;border-radius:4px;font-size:8.5px">
      <span style="color:#0ea5e9;font-weight:700">→</span><span>${safe(m.name)}</span><span style="color:#94a3b8;margin-left:auto">${safe(m.target_date)}</span>
    </div>`
  ).join("") : "";

  // ── BUDGET
  const budgetSpent = parseFloat(report.spent_to_date||0);
  const budgetPlan  = parseFloat(report.budget_to_date||0);
  const variance    = budgetPlan - budgetSpent;
  const budgetStatus = variance >= 0 ? "Under Budget" : "Over Budget";
  const budgetColor  = variance >= 0 ? "#10b981" : "#ef4444";

  // ── WEATHER (daily/weekly)
  const weatherBlock = !isMonthly ? `
    <div style="display:flex;gap:16px;align-items:center;margin-bottom:10px;padding:7px 12px;background:#f0f9ff;border-radius:6px;border-left:3px solid #0ea5e9">
      <div style="font-size:9px"><span style="color:#64748b">Weather:</span> <strong>${safe(report.weather||"—")}</strong></div>
      ${report.temp_c!=null?`<div style="font-size:9px"><span style="color:#64748b">Temp:</span> <strong>${report.temp_c}°C</strong></div>`:""}
      <div style="font-size:9px"><span style="color:#64748b">Site:</span> <strong>${safe(report.site_conditions||"—")}</strong></div>
      ${report.weather_notes?`<div style="font-size:9px;color:#64748b">${safe(report.weather_notes)}</div>`:""}
    </div>
  ` : "";

  const logoHtml = biz.logo ? `<img src="${biz.logo}" style="max-height:36px;max-width:80px;object-fit:contain;display:block" alt="logo"loading="lazy" />` : "";

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Status Report ${safe(report.report_number||"")}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=Playfair+Display:wght@700&display=swap');
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'IBM Plex Sans',sans-serif; font-size:10px; color:#0f1923; background:#fff; }
  @page { size:A4 landscape; margin:0; }
  @media print {
    body { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  }
  table { border-collapse:collapse; width:100%; }
  th { text-align:left; }
</style>
</head>
<body style="padding:0;margin:0">

  <!-- HEADER BAR -->
  <div style="background:${ink};padding:12px 24px;display:flex;justify-content:space-between;align-items:center">
    <div style="display:flex;align-items:center;gap:14px">
      ${logoHtml}
      <div style="width:1px;height:30px;background:rgba(255,255,255,0.15)"></div>
      <div>
        <div style="color:#fff;font-weight:700;font-size:12px">${safe(biz.name||"Your Business")}</div>
        <div style="color:rgba(255,255,255,0.5);font-size:9px">${[biz.email,biz.phone,biz.address].filter(Boolean).join(" · ")}</div>
      </div>
    </div>
    <div style="text-align:right">
      <div style="color:${accentGold};font-weight:800;font-size:15px;letter-spacing:0.5px">${type.toUpperCase()} STATUS REPORT</div>
      <div style="color:rgba(255,255,255,0.55);font-size:9px;margin-top:2px">${safe(report.report_number||"")} · ${periodLabel}</div>
    </div>
  </div>

  <!-- META ROW -->
  <div style="display:grid;grid-template-columns:repeat(5,1fr);border-bottom:2px solid #e2e8f0">
    ${[
      ["Job", report.job_title||"—"],
      ["Client", report.client_name||"—"],
      ["Site", report.site_address||"—"],
      ["Prepared By", report.prepared_by||"—"],
      ["Status", null],
    ].map(([label,val],i)=>`
    <div style="padding:7px 14px;${i<4?"border-right:1px solid #e2e8f0;":""}background:${i%2===0?"#fafbfc":"#fff"}">
      <div style="font-size:8px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:3px">${label}</div>
      ${val!=null
        ? `<div style="font-size:10px;font-weight:600">${safe(val)}</div>`
        : `<span style="font-size:9px;font-weight:700;padding:2px 10px;border-radius:100px;background:${sColor}18;color:${sColor};border:1px solid ${sColor}30">${safe(report.overall_status||"—")}</span>`
      }
    </div>`).join("")}
  </div>

  <!-- TWO COLUMN BODY -->
  <div style="display:grid;grid-template-columns:1.65fr 1fr;height:calc(100vh - 110px);overflow:hidden">

    <!-- LEFT COLUMN -->
    <div style="border-right:1px solid #e2e8f0;padding:12px 16px;overflow:hidden">

      ${weatherBlock}

      <!-- WORK COMPLETED -->
      <div style="margin-bottom:12px">
        <div style="font-size:8.5px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:${accentGold};margin-bottom:6px;display:flex;align-items:center;gap:6px">
          <div style="width:3px;height:11px;background:${accentGold};border-radius:2px"></div> Work Completed This Period
        </div>
        <table style="width:100%">
          <thead>
            <tr style="background:${ink}">
              <th style="padding:4px 8px;color:#fff;font-size:8px;text-transform:uppercase;letter-spacing:0.4px;font-weight:700">Activity</th>
              <th style="padding:4px 8px;color:#fff;font-size:8px;text-transform:uppercase;letter-spacing:0.4px;font-weight:700">Location</th>
              <th style="padding:4px 8px;color:#fff;font-size:8px;text-transform:uppercase;letter-spacing:0.4px;font-weight:700">Status</th>
              <th style="padding:4px 8px;color:#fff;font-size:8px;text-transform:uppercase;letter-spacing:0.4px;font-weight:700">%</th>
              <th style="padding:4px 8px;color:#fff;font-size:8px;text-transform:uppercase;letter-spacing:0.4px;font-weight:700">Notes</th>
            </tr>
          </thead>
          <tbody>${workRows}</tbody>
        </table>
      </div>

      <!-- ISSUES -->
      <div style="margin-bottom:12px">
        <div style="font-size:8.5px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#ef4444;margin-bottom:6px;display:flex;align-items:center;gap:6px">
          <div style="width:3px;height:11px;background:#ef4444;border-radius:2px"></div> Issues, Delays & Risks
        </div>
        ${issueRows}
      </div>

      <!-- NEXT PERIOD PLAN -->
      <div>
        <div style="font-size:8.5px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#0ea5e9;margin-bottom:6px;display:flex;align-items:center;gap:6px">
          <div style="width:3px;height:11px;background:#0ea5e9;border-radius:2px"></div> Next Period Plan
        </div>
        ${planRows}
      </div>

    </div>

    <!-- RIGHT COLUMN -->
    <div style="padding:12px 14px;overflow:hidden">

      <!-- OVERALL PROGRESS -->
      <div style="margin-bottom:10px;padding:9px 13px;background:${accentGold}08;border-radius:7px;border:1px solid ${accentGold}20">
        <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:4px">Overall Progress</div>
        <div style="font-size:26px;font-weight:800;color:${accentGold};font-family:'Playfair Display',serif;line-height:1">${parseFloat(report.overall_pct_complete||0).toFixed(0)}%</div>
        <div style="height:5px;border-radius:3px;background:#e2e8f0;margin-top:6px">
          <div style="width:${Math.min(100,parseFloat(report.overall_pct_complete||0))}%;height:100%;border-radius:3px;background:linear-gradient(90deg,${accentGold},#e4ae3a)"></div>
        </div>
        <div style="font-size:8px;color:#94a3b8;margin-top:4px">Crew: ${crewSummary}</div>
      </div>

      ${showBudget ? `
      <!-- BUDGET -->
      <div style="margin-bottom:10px">
        <div style="font-size:8.5px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#1a3a5c;margin-bottom:6px;display:flex;align-items:center;gap:6px">
          <div style="width:3px;height:11px;background:#1a3a5c;border-radius:2px"></div> Budget Snapshot
        </div>
        ${[
          ["Contract Value", fmtR(report.contract_value)],
          ["Budgeted to Date", fmtR(report.budget_to_date)],
          ["Spent to Date", fmtR(report.spent_to_date)],
          ["Variance", (variance>=0?"":"−")+fmtR(Math.abs(variance)), budgetColor],
        ].map(([k,v,c])=>`
        <div style="display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid #f1f5f9;font-size:9px">
          <span style="color:#94a3b8">${k}</span>
          <span style="font-weight:700${c?";color:"+c:""}">${v}</span>
        </div>`).join("")}
      </div>` : ""}

      ${showMilestones && (milestones.length||upcomingMS.length) ? `
      <!-- MILESTONES -->
      <div style="margin-bottom:10px">
        <div style="font-size:8.5px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#2d1a5c;margin-bottom:5px;display:flex;align-items:center;gap:6px">
          <div style="width:3px;height:11px;background:#2d1a5c;border-radius:2px"></div> Milestones
        </div>
        ${msRows}${upRows}
      </div>` : ""}

      <!-- PHOTOS -->
      <div style="margin-bottom:10px">
        <div style="font-size:8.5px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#3a1a1a;margin-bottom:5px;display:flex;align-items:center;gap:6px">
          <div style="width:3px;height:11px;background:#3a1a1a;border-radius:2px"></div> Site Photos${photos.length?` (${photos.length})`:""}
        </div>
        ${photoGrid}
      </div>

      <!-- SAFETY -->
      <div style="margin-bottom:10px">
        <div style="font-size:8.5px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#10b981;margin-bottom:5px;display:flex;align-items:center;gap:6px">
          <div style="width:3px;height:11px;background:#10b981;border-radius:2px"></div> Safety
        </div>
        <div style="font-size:8.5px;color:#475569;line-height:1.7">${safetySummary}</div>
        ${report.incident_description?`<div style="font-size:8.5px;color:#ef4444;margin-top:3px;padding:4px 8px;background:#fee2e210;border-radius:4px">Incident: ${safe(report.incident_description)}</div>`:""}
      </div>

      <!-- SIGN OFF -->
      <div style="margin-top:auto;padding-top:8px;border-top:1px solid #e2e8f0">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;font-size:8px;color:#94a3b8">
          <div>Prepared by: <strong style="color:#0f1923">${safe(report.prepared_by||"—")}</strong></div>
          <div>Date: <strong style="color:#0f1923">${safe(report.date_to||report.date_from||"—")}</strong></div>
          ${report.reviewed_by?`<div>Reviewed by: <strong style="color:#0f1923">${safe(report.reviewed_by)}</strong></div>`:""}
          ${report.client_ack?`<div>Client: <strong style="color:#0f1923">${safe(report.client_ack)}</strong></div>`:""}
        </div>
        ${report.notes?`<div style="margin-top:5px;font-size:8px;color:#64748b;padding:5px 8px;background:#f8fafc;border-radius:4px">${safe(report.notes)}</div>`:""}
      </div>
    </div>
  </div>

  <!-- FOOTER -->
  <div style="background:#f8fafc;border-top:1px solid #e2e8f0;padding:6px 24px;display:flex;justify-content:space-between;align-items:center;position:fixed;bottom:0;left:0;right:0">
    <span style="font-size:8px;color:#94a3b8">Generated by FieldJob · fieldjob.getdiswayne.com</span>
    <span style="font-size:8px;color:#94a3b8">${safe(report.report_number||"")} · ${safe(biz.name||"")} · Confidential</span>
    <span style="font-size:8px;color:#94a3b8">Page 1 of 1</span>
  </div>

</body>
</html>`;
}

// ─── EXPORT STATUS REPORT TO XLSX ────────────────────────────────────────────

function buildMailto({ to, subject, body }) {
  return `mailto:${encodeURIComponent(to || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function getQuoteEmailContent(doc, business) {
  return {
    subject: `Quote ${doc.number} from ${business.name || "Us"} — ${formatCurrency(doc.total, business.currency)}`,
    body: `Dear ${doc.client_name},\n\nPlease find attached your quote ${doc.number}.\n\nAmount: ${formatCurrency(doc.subtotal, business.currency)} excl. ${business.tax_label||"VAT"}\n${business.tax_label||"VAT"}${business.tax_rate>0?` (${business.tax_rate}%)`:""}: ${formatCurrency(doc.vat, business.currency)}\nTotal: ${formatCurrency(doc.total, business.currency)}\nValid Until: ${formatDate(doc.expiry_date)}\n\nTo accept this quote, please reply to this email or contact us directly.\n\n${doc.notes ? "Notes: " + doc.notes + "\n\n" : ""}Kind regards,\n${business.name || ""}\n${business.phone || ""}\n${business.email || ""}`,
  };
}

function getInvoiceEmailContent(doc, business) {
  const outstanding = (doc.total || 0) - (doc.paid || 0);
  return {
    subject: `Invoice ${doc.number} from ${business.name || "Us"} — ${formatCurrency(doc.total, business.currency)}`,
    body: `Dear ${doc.client_name},\n\nPlease find your invoice ${doc.number} for payment.\n\nInvoice Date: ${formatDate(doc.date)}\nDue Date: ${formatDate(doc.due_date)}\nAmount: ${formatCurrency(doc.subtotal, business.currency)} excl. ${business.tax_label||"VAT"}\n${business.tax_label||"VAT"}${business.tax_rate>0?` (${business.tax_rate}%)`:""}: ${formatCurrency(doc.vat, business.currency)}\nTotal: ${formatCurrency(doc.total, business.currency)}${doc.paid > 0 ? `\nAlready Paid: ${formatCurrency(doc.paid, business.currency)}\nOutstanding: ${formatCurrency(outstanding, business.currency)}` : ""}\n\nPlease make payment to:\nBank: ${business.bank_name || ""}\nAccount Holder: ${business.bank_account_holder || ""}\nAccount Number: ${business.bank_account_number || ""}\nBranch Code: ${business.bank_branch_code || ""}\nReference: ${doc.number}\n\n${doc.notes ? "Notes: " + doc.notes + "\n\n" : ""}Kind regards,\n${business.name || ""}\n${business.phone || ""}\n${business.email || ""}`,
  };
}

function getReceiptEmailContent(doc, business) {
  return {
    subject: `Payment Receipt ${doc.number} — ${business.name || ""}`,
    body: `Dear ${doc.client_name},\n\nThank you for your payment. Please find your receipt below.\n\nReceipt Number: ${doc.number}\nInvoice Reference: ${doc.invoice_number}\nDate: ${formatDate(doc.date)}\nAmount Received: ${formatCurrency(doc.amount, business.currency)}\nPayment Method: ${doc.payment_method}\n\nThank you for your business!\n\nKind regards,\n${business.name || ""}\n${business.phone || ""}\n${business.email || ""}`,
  };
}

function getEstimateEmailContent(doc, business) {
  return {
    subject: `Estimate ${doc.number} from ${business.name || "Us"} — ${formatCurrency(doc.grand_total, business.currency)}`,
    body: `Dear ${doc.client_name},\n\nPlease find your estimate ${doc.number} for the following work.\n\nSite: ${doc.site_address || ""}\nDate: ${formatDate(doc.date)}\nValid Until: ${formatDate(doc.valid_until)}\nTotal Estimate: ${formatZAR(doc.grand_total)}\n\nThis is an estimate only and not a tax invoice. To proceed, please accept this estimate and a formal quote will be issued.\n\n${doc.assumptions ? "Basis of Estimate:\n" + doc.assumptions + "\n\n" : ""}${doc.payment_terms ? "Payment Terms:\n" + doc.payment_terms + "\n\n" : ""}Kind regards,\n${business.name || ""}\n${business.phone || ""}\n${business.email || ""}`,
  };
}

function getJobEmailContent(doc, business) {
  return {
    subject: `Job Report: ${doc.title} — ${business.name || ""}`,
    body: `Dear ${doc.client_name},\n\nPlease find the job report for the following:\n\nJob: ${doc.title}\nDate: ${formatDate(doc.date)}\nAddress: ${doc.address || ""}\nTechnician(s): ${(doc.worker_names||[]).length > 0 ? doc.worker_names.join(', ') : (doc.worker_name||'')}\nStatus: ${doc.status}\n\nDescription of Work:\n${doc.description || "N/A"}\n\nMaterials Used:\n${doc.materials || "N/A"}\n\nEstimated Value: ${formatCurrency(doc.estimated_cost, business.currency)}\n\n${doc.notes ? "Notes: " + doc.notes + "\n\n" : ""}Kind regards,\n${business.name || ""}\n${business.phone || ""}\n${business.email || ""}`,
  };
}

// ─── PDF GENERATION + UPLOAD ──────────────────────────────────────────────────
const generateAndUploadPDF = async (htmlContent, filename) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Create off-screen container
      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-9999px;top:0;width:794px;background:white;padding:52px 60px;box-sizing:border-box;";
      container.innerHTML = `<style>
        *{font-family:'Segoe UI',sans-serif;box-sizing:border-box;}
        .doc-wrap{width:100%;padding:0;}
        table{width:100%;border-collapse:collapse;}
        td,th{padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:12px;}
        th{background:#0f1923;color:white;text-align:left;}
        .header{display:flex;justify-content:space-between;margin-bottom:32px;padding-bottom:24px;border-bottom:2px solid #e2e8f0;}
        img{max-width:110px;max-height:65px;object-fit:contain;}
        p,div,span,td,th{word-break:break-word;}
      </style>${htmlContent}`;
      document.body.appendChild(container);

      await new Promise(r => setTimeout(r, 400));

      const canvas = await window.html2canvas(container, {
        scale: 2, useCORS: true, allowTaint: true,
        backgroundColor: "#ffffff", logging: false,
        width: 794, windowWidth: 794,
      });
      document.body.removeChild(container);

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ orientation:"portrait", unit:"mm", format:"a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * pageW) / canvas.width;
      let y = 0;
      let remaining = imgH;
      let page = 0;
      while (remaining > 0) {
        if (page > 0) pdf.addPage();
        const sliceH = Math.min(remaining, pageH);
        const srcY = (page * pageH * canvas.width) / pageW;
        const srcH = (sliceH * canvas.width) / pageW;
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        sliceCanvas.getContext("2d").drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        pdf.addImage(sliceCanvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, imgW, sliceH);
        remaining -= sliceH;
        page++;
      }

      const pdfBlob = pdf.output("blob");

      // Upload to Supabase documents bucket
      const safeName = filename.replace(/[^a-zA-Z0-9\-_.]/g, "-").toLowerCase();
      const path = `${safeName}-${Date.now()}.pdf`;
      const res = await fetch(`${SUPABASE_URL}/storage/v1/object/documents/${path}`, {
        method: "POST",
        headers: {
          "apikey": SUPABASE_ANON_KEY,
          "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
          "Content-Type": "application/pdf",
          "x-upsert": "true",
        },
        body: pdfBlob,
      });
      if (!res.ok) { reject(new Error("Upload failed: " + await res.text())); return; }
      resolve(`${SUPABASE_URL}/storage/v1/object/public/documents/${path}`);
    } catch(e) { reject(e); }
  });
};

// ─── SHARE MODAL ──────────────────────────────────────────────────────────────


// Generate PDF as base64 string (for email attachment — no upload needed)
const generatePDFBase64 = async (htmlContent) => {
  return new Promise(async (resolve, reject) => {
    try {
      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-9999px;top:0;width:794px;background:white;padding:52px 60px;box-sizing:border-box;";
      container.innerHTML = `<style>
        *{font-family:'Segoe UI',sans-serif;box-sizing:border-box;}
        .doc-wrap{width:100%;padding:0;}
        table{width:100%;border-collapse:collapse;}
        td,th{padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:12px;}
        th{background:#0f1923;color:white;text-align:left;}
        .header{display:flex;justify-content:space-between;margin-bottom:32px;padding-bottom:24px;border-bottom:2px solid #e2e8f0;}
        img{max-width:110px;max-height:65px;object-fit:contain;}
        p,div,span,td,th{word-break:break-word;}
      </style>${htmlContent}`;
      document.body.appendChild(container);
      await new Promise(r => setTimeout(r, 400));
      const canvas = await window.html2canvas(container, {
        scale:2, useCORS:true, allowTaint:true, backgroundColor:"#ffffff",
        logging:false, width:794, windowWidth:794,
      });
      document.body.removeChild(container);
      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ orientation:"portrait", unit:"mm", format:"a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * pageW) / canvas.width;
      let y = 0, remaining = imgH, page = 0;
      while (remaining > 0) {
        if (page > 0) pdf.addPage();
        const sliceH = Math.min(remaining, pageH);
        const srcY   = (page * pageH * canvas.width) / pageW;
        const srcH   = (sliceH * canvas.width) / pageW;
        const sc = document.createElement("canvas");
        sc.width = canvas.width; sc.height = srcH;
        sc.getContext("2d").drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        pdf.addImage(sc.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, pageW, sliceH);
        remaining -= sliceH; page++;
      }
      // Return base64 string without the data: prefix
      const base64 = pdf.output("datauristring").split(",")[1];
      resolve(base64);
    } catch(e) { reject(e); }
  });
};


// ─── EMAIL RATE LIMITS ────────────────────────────────────────────────────────
const EMAIL_LIMITS = { free:10, demo:10, starter:100, paid:200, business:200 };

async function getUserPlan(userId) {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/fj_users?auth_id=eq.${encodeURIComponent(userId)}&select=plan&limit=1`,
      { headers: { "apikey": SUPABASE_ANON_KEY, "Authorization": getAuthHeader() } }
    );
    const data = await res.json();
    return data?.[0]?.plan || "free";
  } catch { return "free"; }
}

async function getEmailUsage(userId) {
  try {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/fj_doc_email_log?user_id=eq.${encodeURIComponent(userId)}&sent_at=gte.${monthStart}&select=id`,
      { headers: { "apikey": SUPABASE_ANON_KEY, "Authorization": getAuthHeader(), "Prefer": "count=exact" } }
    );
    const range = res.headers.get("content-range");
    return range ? parseInt(range.split("/")[1] ?? "0", 10) : 0;
  } catch { return 0; }
}

function getEmailLimit(plan) {
  return EMAIL_LIMITS[plan] ?? 10;
}

// Send email with PDF attachment via Supabase Edge Function
async function sendDocumentEmail({ to, subject, htmlBody, pdfBase64, filename, userId, documentType, documentId }) {
  // 1. Check rate limit
  const resolvedPlan = await getUserPlan(userId);
  const limit = getEmailLimit(resolvedPlan);
  const used  = await getEmailUsage(userId);
  if (used >= limit) {
    return { error: "rate_limit_exceeded", used, limit, plan: resolvedPlan };
  }
  // 2. Call Edge Function
  try {
    const res = await fetchWithRetry(`${SUPABASE_URL}/functions/v1/send-document-email`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON_KEY}`, "apikey": SUPABASE_ANON_KEY },
      body:    JSON.stringify({ to, subject, html_body: htmlBody, pdf_base64: pdfBase64, filename, user_id: userId, document_type: documentType, document_id: documentId }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "send_failed", used, limit, details: data };
    return { success: true, used: data.used, limit };
  } catch (e) {
    return { error: "network_error", message: String(e) };
  }
}

// ── EXPORTS ──────────────────────────────────────────────────────────────────
export {
  OWNER_EMAIL, OWNER_PASSWORD, isOwner,
  V, FormError,
  NotifContext, NotifProvider,
  IDB, Icon,
  DeleteConfirmModal, PasswordInput,
  TABLE_MAP, DataCache, DB,
  SUPABASE_URL, SUPABASE_ANON_KEY, supabase, fetchWithRetry, getAuthHeader,
  formatZAR, formatCurrency, getCurrencyOption, CURRENCY_OPTIONS,
  UNIT_LABELS, unitLabel,
  formatDate, today, addDays, daysLeft,
  STATUS_COLORS, STATUS_PIPELINE,
  printAsPDF,
  buildDocHTML, buildReceiptHTML, buildJobReportHTML, buildEstimateDocHTML,
  buildStatusReportHTML,
  generateAndUploadPDF,
  getQuoteEmailContent, getInvoiceEmailContent, getReceiptEmailContent,
  getEstimateEmailContent, getJobEmailContent,
  buildMailto,
  exportExpensesXLSX,
  CSS,
  EMAIL_LIMITS, getEmailUsage, getEmailLimit, getUserPlan, sendDocumentEmail,
  generatePDFBase64,
};
